// src/pages/Chats.js
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { io } from 'socket.io-client';
import { API_URL, SOCKET_URL } from '../config';
import { apiFetch, getAccessToken } from '../utils/auth';
import { getRangiDeviceId } from '../utils/deviceId';
import { useRole, useEmployeeId, useRoleActions } from '../context/RoleContext';
import { useLocation, useNavigate } from 'react-router-dom';

import s from './Chats.module.css';

import ChatSidebar from './ChatSidebar';
import ChatThread from './ChatThread';
import { FAVORITES_CHAT_TITLE, isFavoritesChat } from '../utils/chatFavorites';
import { useEmployeeTaskStats } from '../utils/employeeTaskStats';

const CHAT_DRAFTS_STORAGE_KEY = 'chatDraftsByChatId';
const FLASH_MESSAGE_MS = 3000;
const CHAT_NEAR_BOTTOM_PX = 240;
const CHAT_MESSAGES_PAGE_SIZE = 50;
const CHAT_MESSAGES_WINDOW_LIMIT = 250;

const HISTORY_PREPEND_SCROLL_LOCK_MS = 420;

function captureChatScrollAnchor(container) {
  if (!container?.isConnected) {
    return {
      prevScrollHeight: 0,
      prevScrollTop: 0,
      anchorMessageId: null,
      anchorVisibleOffset: 0,
    };
  }
  const containerRect = container.getBoundingClientRect();
  let anchorMessageId = null;
  let anchorVisibleOffset = 0;
  for (const item of container.querySelectorAll('[data-msg-id]')) {
    const r = item.getBoundingClientRect();
    if (r.bottom > containerRect.top + 1 && r.top < containerRect.bottom - 1) {
      anchorMessageId = item.dataset.msgId ?? null;
      anchorVisibleOffset = r.top - containerRect.top;
      break;
    }
  }
  return {
    prevScrollHeight: container.scrollHeight,
    prevScrollTop: container.scrollTop,
    anchorMessageId,
    anchorVisibleOffset,
  };
}

function loadChatDrafts() {
  try {
    const raw = localStorage.getItem(CHAT_DRAFTS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const normalized = {};
    for (const [chatId, text] of Object.entries(parsed)) {
      if (typeof text === 'string' && text.length) normalized[String(chatId)] = text;
    }
    return normalized;
  } catch {
    return {};
  }
}

function saveChatDrafts(nextDrafts) {
  try {
    localStorage.setItem(CHAT_DRAFTS_STORAGE_KEY, JSON.stringify(nextDrafts || {}));
  } catch {
    // ignore
  }
}

function buildForwardPreviewText(msg) {
  if (!msg) return 'Переслано';
  const hasAtt = Array.isArray(msg.attachments) && msg.attachments.length > 0;
  const base = (msg.content ?? '') || (hasAtt ? '📎 вложение' : '');
  const fwdFrom = msg.sender_name || 'неизвестный автор';
  return `Переслано от ${fwdFrom}: ${base}`;
}

function patchChatsAfterForward(prev, targetChatIds, previewText, previewTime) {
  const targetSet = new Set(targetChatIds.map(String));
  return prev.map((c) =>
    targetSet.has(String(c.id))
      ? {
          ...c,
          last_message_text: previewText,
          last_message_time: previewTime,
          unread_count: 0,
        }
      : c
  );
}

function normalizeChatMessage(message, patch = {}) {
  return {
    ...message,
    ...patch,
    reactions: Array.isArray(message?.reactions) ? message.reactions : [],
  };
}

function trimChatWindowFromEnd(messages, limit = CHAT_MESSAGES_WINDOW_LIMIT) {
  if (!Array.isArray(messages) || messages.length <= limit) {
    return { messages, trimmed: false, cursor: null };
  }
  const next = messages.slice(0, limit);
  return {
    messages: next,
    trimmed: true,
    cursor: next.length ? next[next.length - 1].id : null,
  };
}

function trimChatWindowFromStart(messages, limit = CHAT_MESSAGES_WINDOW_LIMIT) {
  if (!Array.isArray(messages) || messages.length <= limit) {
    return { messages, trimmed: false, cursor: null };
  }
  const next = messages.slice(messages.length - limit);
  return {
    messages: next,
    trimmed: true,
    cursor: next.length ? next[0].id : null,
  };
}

// VAPID public key получаем с бэка
async function fetchVapidPublicKey() {
  try {
    const res = await apiFetch(`${API_URL}/chats/webpush/public-key`);
    if (!res.ok) throw new Error('Failed to load VAPID public key');
    const data = await res.json();
    return data?.public_key || null; // base64url-строка
  } catch (e) {
    console.warn('Не удалось получить VAPID public key:', e);
    return null;
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return new Uint8Array([...rawData].map((char) => char.charCodeAt(0)));
}

/** Эмодзи, у которого вырос count (для подписи тоста). */
function findPrimaryNewReactionEmoji(prevList, nextList) {
  const counts = (list) => {
    const m = Object.create(null);
    for (const r of list || []) {
      const em = r?.emoji;
      if (!em) continue;
      m[em] = Number(r.count) || 0;
    }
    return m;
  };
  const p = counts(prevList);
  const n = counts(nextList);
  for (const em of Object.keys(n)) {
    if ((n[em] || 0) > (p[em] || 0)) return em;
  }
  return null;
}

function sumReactionCounts(list) {
  const arr = Array.isArray(list) ? list : [];
  return arr.reduce((s, r) => s + (Number(r.count) || 0), 0);
}

function pickDominantReactionEmoji(nextList) {
  const list = Array.isArray(nextList) ? nextList : [];
  if (!list.length) return null;
  const sorted = [...list].sort(
    (a, b) => (Number(b.count) || 0) - (Number(a.count) || 0)
  );
  return sorted[0]?.emoji || null;
}

// регистрация без user_id, с повторным использованием существующей подписки
async function registerWebPush(/* userId (не требуется на бэкенде) */) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.warn("Браузер не поддерживает WebPush");
    return;
  }

  try {
    // Разрешение на уведомления
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      try { await Notification.requestPermission(); } catch { }
    }
    if (typeof Notification !== 'undefined' && Notification.permission !== 'granted') {
      console.warn('Уведомления отключены пользователем');
      // всё равно регистрируем SW, чтобы можно было спросить позже
    }

    // Регистрируем service worker
    const registration = await navigator.serviceWorker.register("/sw.js");

    // Пытаемся взять существующую подписку
    let subscription = await registration.pushManager.getSubscription();

    // Если нет — подписываемся с ключом, полученным с бэка
    if (!subscription) {
      const pub = await fetchVapidPublicKey();
      if (!pub) {
        console.warn('Нет VAPID public key, подписка невозможна');
        return;
      }
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(pub)
      });
    }

    // Отправляем на бэкенд только subscription через авторизованный apiFetch
    await apiFetch(`${API_URL}/webpush/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        subscription,
        device_id: getRangiDeviceId(),
      })
    });

    console.log("✅ WebPush подписка зарегистрирована");
  } catch (err) {
    console.error("❌ Ошибка при регистрации WebPush:", err);
  }
}

// пред-валидация на фронте больше не ограничивает создание личных чатов по ролям.
// Источник истины — бэкенд.
function canStartChat() {
  return true;
}

function dedupeParticipantsById(list) {
  const seen = new Set();
  return (list || []).filter((p) => {
    const k = String(p.id);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

// Нормализация типа чата в строку
const normalizeType = (t) => {
  if (typeof t === 'string') return t;
  if (t && typeof t === 'object') return t.value || t.name || String(t);
  return 'group';
};

function extractPinnedListFromEnvelope(data) {
  if (!data || typeof data !== 'object') return null;
  if (Array.isArray(data.pinned_messages)) return data.pinned_messages;
  if (Array.isArray(data.pinnedMessages)) return data.pinnedMessages;
  return null;
}

// формат времени как в макете: HH:MM для сегодня, "Вчера" для вчера, иначе DD.MM.YYYY
function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();

  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return 'Вчера';

  return d.toLocaleDateString('ru-RU');
}

async function postToSW(message) {
  try {
    const reg = await navigator.serviceWorker?.ready;
    const sw = reg?.active || reg?.waiting || reg?.installing;
    if (sw) sw.postMessage(message);
    else navigator.serviceWorker?.controller?.postMessage(message); // fallback
  } catch { }
}

async function syncBadge() {
  try {
    const r = await apiFetch(`${API_URL}/chats/badge_total`);
    if (!r.ok) return;
    const data = await r.json();
    const count = data?.badge_count ?? 0;
    await postToSW({ type: "SET_BADGE", count });
  } catch { }
}

// Получить список возможных собеседников для личного чата
async function fetchPossibleTargets(currentEmployeeId, myRole) {
  try {
    // Используем существующий поиск сотрудников (пустой запрос → все)
    const response = await apiFetch(`${API_URL}/employees/search/?query=`);
    if (!response.ok) return [];
    const all = await response.json();
    // фильтруем: не я и разрешено правилами
    return (all || [])
      .filter(e => String(e.id) !== String(currentEmployeeId))
      .filter(e => canStartChat(myRole, [e.role || 'employee']));
  } catch {
    return [];
  }
}

function Chats({ userId }) {
  const role = useRole();
  const employeeId = useEmployeeId();
  const { hasPermission } = useRoleActions();
  const location = useLocation();
  const navigate = useNavigate();
  const sendingRef = useRef(false);
  // 1) глобальный стейт для всех чатов пользователя
  const [msgFontSize, setMsgFontSize] = React.useState(() => {
    const v = Number(localStorage.getItem('chatFontSize'));
    return Number.isFinite(v) ? v : 15; // дефолт
  });

  // 2) применяем к CSS-переменной и сохраняем в localStorage
  React.useEffect(() => {
    const px = Math.min(18, Math.max(13, msgFontSize));
    document.documentElement.style.setProperty('--chat-font-size', `${px}px`);
    localStorage.setItem('chatFontSize', String(px));
  }, [msgFontSize]);

  // 3) коллбеки для кнопок «A− / A+»
  const decFont = React.useCallback(() => setMsgFontSize(s => Math.max(13, s - 1)), []);
  const incFont = React.useCallback(() => setMsgFontSize(s => Math.min(18, s + 1)), []);

  const handleOpenAccountFromChat = async (chat) => {
    // виртуальная приватка: id уже есть
    if (chat.__virtual && chat.__targetEmployeeId) {
      navigate(`/account/${chat.__targetEmployeeId}`);
      return;
    }
    // работаем только с реальными приватными чатами
    if (normalizeType(chat.type ?? chat.chat_type).toLowerCase() !== 'private') return;
    // сначала попробуем из превью (если backend уже положил participant_ids)
    const previewOther = []
      .concat(chat.participant_ids || [])
      .map(Number)
      .find((id) => String(id) !== String(myId));
    if (previewOther) {
      navigate(`/account/${previewOther}`);
      return;
    }

    // иначе — спросим бэкенд
    try {
      const res = await apiFetch(`${API_URL}/chats/${chat.id}/participants`);
      if (res.ok) {
        const list = await res.json(); // ожидаем [{employee_id, name, ...}]
        const other = (list || []).map(p => Number(p.employee_id))
          .find(id => String(id) !== String(myId));
        if (other) navigate(`/account/${other}`);
      }
    } catch { /* noop */ }
  };

  // просмотр участников группового/рангового чата
  const [isMembersOpen, setIsMembersOpen] = useState(false);
  const [membersChatTitle, setMembersChatTitle] = useState('');
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersList, setMembersList] = useState([]); // [{id, name}]

  const openMembers = async (chat) => {
    if (!chat?.id) return;

    setMembersChatTitle(chat.name || '');
    setIsMembersOpen(true);
    setMembersLoading(true);

    try {
      const resp = await apiFetch(`${API_URL}/chats/${chat.id}/participants`);
      const list = resp.ok ? await resp.json() : [];

      setMembersList(
        (list || []).map(p => ({
          id: Number(p.employee_id),
          name: p.name || p.full_name || `Пользователь ${p.employee_id}`,
        }))
      );
    } catch {
      setMembersList([]);
    } finally {
      setMembersLoading(false);
    }
  };

  const handleAvatarClick = async (chat) => {
    // виртуальные лички пусть остаются как было (переход в аккаунт)
    if (chat?.__virtual) return handleOpenAccountFromChat(chat);
    if (isFavoritesChat(chat)) return;

    const t = normalizeType(chat?.type ?? chat?.chat_type).toLowerCase();

    if (t === 'private') return handleOpenAccountFromChat(chat);
    if (t === 'group' || t === 'rank') return openMembers(chat);
  };

  // ─────────────────────────────────────────────────────────────
  // EMPLOYEE_ID bootstrap: получаем employee_id на холодном старте
  // ─────────────────────────────────────────────────────────────
  const [idReady, setIdReady] = useState(false);
  useEffect(() => {
    (async () => {
      // если контекст уже знает id — сохраняем и готово
      if (employeeId) {
        try { localStorage.setItem('employee_id', String(employeeId)); } catch { }
        setIdReady(true);
        return;
      }
      // если в LS уже лежит — используем
      const saved = localStorage.getItem('employee_id');
      if (saved) {
        setIdReady(true);
        return;
      }
      // иначе — подтянем /users/me и сохраним employee_id
      try {
        const res = await apiFetch(`${API_URL}/users/me`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          const eid = data?.employee_id;
          if (eid != null) {
            try { localStorage.setItem('employee_id', String(eid)); } catch { }
          }
        } else {
          console.warn('Не удалось получить /users/me:', res.status);
        }
      } catch (e) {
        console.warn('Ошибка запроса /users/me:', e);
      } finally {
        setIdReady(true);
      }
    })();
  }, [employeeId]);
  // ─────────────────────────────────────────────────────────────

  //  источник для моего employee_id: активный пользователь → RoleContext → localStorage
  // Используем ref-стабилизацию: если employeeId мигает в null (рефреш токена),
  // берём последнее известное значение из localStorage, чтобы сокет не пересоздавался.
  const myId = useMemo(
    () => {
      const id = employeeId ?? Number(localStorage.getItem('employee_id')) ?? null;
      if (id) {
        try { localStorage.setItem('employee_id', String(id)); } catch { }
      }
      return id;
    },
    [employeeId]
  );

  const canManageChats = hasPermission('chats.manage');
  const canDeleteChats = hasPermission('chats.delete');
  const canDeleteAnyMessages = hasPermission('messages.delete_any');

  const [userChats, setUserChats] = useState([]);
  const userChatsRef = useRef(userChats);
  userChatsRef.current = userChats;
  const [currentChatId, setCurrentChatId] = useState(null);
  // ✅ online/offline: employee_id -> true/false
  const [onlineByEmployeeId, setOnlineByEmployeeId] = useState({});

  React.useEffect(() => {
    setReplyDraft(null);
  }, [currentChatId]);

  const setEmployeeOnline = React.useCallback((employeeId, online) => {
    const id = Number(employeeId);
    if (!Number.isFinite(id)) return;
    setOnlineByEmployeeId(prev => ({ ...prev, [id]: !!online }));
  }, []);

  const isEmployeeOnline = React.useCallback((employeeId) => {
    const id = Number(employeeId);
    return Number.isFinite(id) ? !!onlineByEmployeeId[id] : false;
  }, [onlineByEmployeeId]);

  // кто “собеседник” для лички
  const getPeerEmployeeId = React.useCallback((chat) => {
    if (!chat) return null;

    // виртуальная личка
    if (chat.__virtual && chat.__targetEmployeeId != null) {
      const v = Number(chat.__targetEmployeeId);
      return Number.isFinite(v) ? v : null;
    }

    // если бэк уже отдаёт явное поле
    const explicit =
      chat.other_employee_id ??
      chat.otherEmployeeId ??
      chat.counterparty_employee_id ??
      chat.counterpartyEmployeeId;

    if (explicit != null) {
      const v = Number(explicit);
      return Number.isFinite(v) ? v : null;
    }

    // иначе пробуем из participant_ids
    const ids = chat.participant_ids ?? chat.participantIds;
    if (Array.isArray(ids) && myId != null) {
      const my = String(myId);
      const other = ids.map(Number).find((id) => String(id) !== my);
      return Number.isFinite(other) ? other : null;
    }

    return null;
  }, [myId]);

  const SIDEBAR_MIN = 120;
  const SIDEBAR_MAX = 560;
  const DEFAULT_SIDEBAR_WIDTH = 360;

  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const raw = Number(localStorage.getItem('chatsSidebarWidth'));
    return Number.isFinite(raw)
      ? Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, raw))
      : DEFAULT_SIDEBAR_WIDTH;
  });

  const isResizingRef = useRef(false);

  useEffect(() => {
    localStorage.setItem('chatsSidebarWidth', String(sidebarWidth));
  }, [sidebarWidth]);

  const startResize = React.useCallback((e) => {
    // только десктоп
    if (window.innerWidth < 768) return;

    isResizingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!isResizingRef.current) return;

      const sidebarEl = sidebarColRef.current;
      if (!sidebarEl) return;

      const rect = sidebarEl.getBoundingClientRect();
      const nextWidth = e.clientX - rect.left;

      setSidebarWidth(
        Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, nextWidth))
      );
    };

    const onUp = () => {
      if (!isResizingRef.current) return;
      isResizingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  // для сайдбара: вернуть {show, online}
  const getChatPresence = React.useCallback((chat) => {
    const t = normalizeType(chat?.type ?? chat?.chat_type).toLowerCase();
    if (t !== 'private') return null; // ✅ показываем кружок только у личных
    const peerId = getPeerEmployeeId(chat);
    if (!peerId) return { show: true, online: false }; // не знаем -> серый
    return { show: true, online: isEmployeeOnline(peerId) };
  }, [getPeerEmployeeId, isEmployeeOnline]);

  const [chatMessages, setChatMessages] = useState([]);
  const chatMessagesRef = useRef([]);

  const employeeTaskStatsIds = useMemo(() => {
    const ids = new Set();
    for (const chat of userChats) {
      const peer = getPeerEmployeeId(chat);
      if (peer) ids.add(peer);
    }
    if (Array.isArray(chatMessages)) {
      for (const message of chatMessages) {
        const senderId = Number(message?.sender_id);
        if (Number.isFinite(senderId) && senderId > 0) ids.add(senderId);
      }
    }
    return [...ids];
  }, [userChats, chatMessages, getPeerEmployeeId]);

  const employeeTaskStatsMap = useEmployeeTaskStats(employeeTaskStatsIds);

  const [pinnedMessagesItems, setPinnedMessagesItems] = useState([]);
  /** Пока false — не перезаписываем is_pinned у сообщений пустым списком (избегаем мигания при смене чата). */
  const [pinnedMetaReady, setPinnedMetaReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isChatSwitching, setIsChatSwitching] = useState(false);

  const searchSnapshotRef = useRef(null);
  const [isSearchMode, setIsSearchMode] = useState(false);

  // pagination для сообщений
  const [messagesHasMore, setMessagesHasMore] = useState(false);
  const [messagesCursor, setMessagesCursor] = useState(null); // next_before_id
  const [loadingOlder, setLoadingOlder] = useState(false);
  // pagination вниз (newer)
  const [messagesHasMoreNewer, setMessagesHasMoreNewer] = useState(false);
  const [messagesAfterCursor, setMessagesAfterCursor] = useState(null); // next_after_id / last_id
  const messagesHasMoreNewerRef = useRef(false);
  const [loadingNewer, setLoadingNewer] = useState(false);
  const historyPrependScrollLockRef = useRef(false);
  const historyAppendScrollLockRef = useRef(false);
  const loadingOlderInFlightRef = useRef(false);
  const loadingNewerInFlightRef = useRef(false);
  const prependScrollRestoreRef = useRef(null);
  const [messagesAroundRevision, setMessagesAroundRevision] = useState(0);

  useEffect(() => {
    chatMessagesRef.current = chatMessages;
  }, [chatMessages]);

  useEffect(() => {
    messagesHasMoreNewerRef.current = messagesHasMoreNewer;
  }, [messagesHasMoreNewer]);

  const handleSearchModeChange = (open) => {
    setIsSearchMode(!!open);

    if (open) {

      setShouldAutoscroll(false);
      // сохраняем текущее состояние, чтобы потом вернуть как было
      searchSnapshotRef.current = {
        chatMessages,
        messagesHasMore,
        messagesCursor,
        shouldAutoscroll,
        messagesHasMoreNewer,
        messagesAfterCursor,
        // если у вас есть курсоры/olderCursor — тоже сохраните
      };
    } else {
      // выходим из поиска → возвращаем как было
      const snap = searchSnapshotRef.current;
      if (snap) {
        setChatMessages(snap.chatMessages || []);
        setMessagesHasMore(!!snap.messagesHasMore);
        setMessagesCursor(snap.messagesCursor ?? null);
        setShouldAutoscroll(!!snap.shouldAutoscroll);
        setMessagesHasMoreNewer(!!snap.messagesHasMoreNewer);
        setMessagesAfterCursor(snap.messagesAfterCursor ?? null);
      } else {
        // если снапшота нет — хотя бы включим пагинацию,
        // иначе пользователь может залипнуть на "окне" без догрузки
        setMessagesHasMore(true);
      }
      searchSnapshotRef.current = null;
    }
  };

  const loadAroundForSearch = async (anchorId, opts = {}) => {
    if (!currentChatId || !anchorId) return null;

    const before = Number.isFinite(Number(opts?.before)) ? Number(opts.before) : 25;
    const after = Number.isFinite(Number(opts?.after)) ? Number(opts.after) : 25;

    const resp = await apiFetch(
      `${API_URL}/chats/${currentChatId}/messages/around?message_id=${anchorId}&before=${before}&after=${after}`
    );
    if (!resp.ok) return null;

    const data = await resp.json();

    const el = messagesContainerRef.current;
    const centerOffset = el
      ? Math.max(48, (el.clientHeight - 80) / 2)
      : 200;
    if (!opts?.skipScrollRestore) {
      prependScrollRestoreRef.current = {
        prevScrollHeight: el?.scrollHeight ?? 0,
        prevScrollTop: el?.scrollTop ?? 0,
        anchorMessageId: String(anchorId),
        anchorVisibleOffset: centerOffset,
        skipPrependStabilize: true,
        skipDeltaFallback: true,
      };
    } else {
      prependScrollRestoreRef.current = null;
    }

    // ВАЖНО: подменяем список сообщений на "окно вокруг совпадения"
    setChatMessages((data.messages || []).map((m) => normalizeChatMessage(m)));

    const embPins = extractPinnedListFromEnvelope(data);
    if (embPins != null && !opts?.keepPinnedList) {
      setPinnedMessagesItems(embPins);
    }

    // в поиске мы не вырубаем пагинацию, а выставляем курсоры от границ окна
    const msgs = Array.isArray(data?.messages) ? data.messages : [];
    if (msgs.length) {
      const firstId = msgs[0].id;
      const lastId = msgs[msgs.length - 1].id;
      setMessagesCursor(firstId);            // для before_id
      setMessagesAfterCursor(lastId);        // для after_id
    }
    setMessagesHasMore(!!data?.has_more_older);
    setMessagesHasMoreNewer(!!data?.has_more_newer);
    setMessagesAroundRevision((v) => v + 1);

    return data; // вернем, чтобы ChatThread мог, например, знать anchor_id
  };

  const jumpToLatestMessageWindow = async () => {
    if (!currentChatId) return null;
    try {
      const r = await apiFetch(`${API_URL}/chats/${currentChatId}/messages?limit=1`);
      if (!r.ok) return null;
      const data = await r.json();
      const msgs = Array.isArray(data?.messages) ? data.messages : (Array.isArray(data) ? data : []);
      const latest = msgs.length ? msgs[msgs.length - 1] : null;
      const latestId = Number(latest?.id);
      if (!Number.isFinite(latestId) || latestId <= 0) return null;
      return await loadAroundForSearch(latestId, { before: 8, after: 120 });
    } catch (e) {
      console.error('jump to latest message window failed:', e);
      return null;
    }
  };

  const refreshPinnedMessages = React.useCallback(async (chatId) => {
    if (!chatId) return;
    try {
      const r = await apiFetch(`${API_URL}/chats/${chatId}/pinned-messages`);
      if (!r.ok) return;
      const d = await r.json();
      const items = Array.isArray(d?.items) ? d.items : Array.isArray(d) ? d : [];
      setPinnedMessagesItems(items);
    } catch {
      /* ignore */
    }
  }, []);

  React.useEffect(() => {
    if (!pinnedMetaReady) return;
    const ids = new Set(pinnedMessagesItems.map((x) => String(x.id)));
    setChatMessages((prev) => {
      if (!prev.length) return prev;
      let changed = false;
      const next = prev.map((m) => {
        const p = ids.has(String(m.id));
        if (!!m.is_pinned === p) return m;
        changed = true;
        return { ...m, is_pinned: p };
      });
      return changed ? next : prev;
    });
  }, [pinnedMessagesItems, pinnedMetaReady]);


  // ref на контейнер скролла сообщений (нужен для сохранения позиции)
  const messagesContainerRef = useRef(null);

  // Имя текущего сотрудника по последним моим сообщениям в чате
  const myDisplayName = useMemo(() => {
    if (!Array.isArray(chatMessages) || !chatMessages.length || myId == null) return null;
    const myIdStr = String(myId);
    for (let i = chatMessages.length - 1; i >= 0; i -= 1) {
      const m = chatMessages[i];
      if (!m) continue;
      if (String(m.sender_id) === myIdStr && m.sender_name) {
        return m.sender_name;
      }
    }
    return null;
  }, [chatMessages, myId]);

  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ visible: false, message: '' });
  const toastTimerRef = useRef(null);
  const showToastRef = useRef(null);

  const [chatDraftsByChatId, setChatDraftsByChatId] = useState(() => loadChatDrafts());
  const chatDraftsByChatIdRef = useRef(chatDraftsByChatId);
  useEffect(() => {
    chatDraftsByChatIdRef.current = chatDraftsByChatId;
  }, [chatDraftsByChatId]);

  const [newMessage, setNewMessage] = useState('');
  useEffect(() => {
    if (!currentChatId) {
      setNewMessage('');
      return;
    }
    const nextDraft = chatDraftsByChatIdRef.current[String(currentChatId)] || '';
    setNewMessage(nextDraft);
  }, [currentChatId]);
  useEffect(() => {
    if (!currentChatId) return;
    const chatKey = String(currentChatId);
    const nextValue = typeof newMessage === 'string' ? newMessage : '';
    const prevValue = chatDraftsByChatIdRef.current[chatKey] || '';
    if (nextValue === prevValue) return;

    const nextDrafts = { ...chatDraftsByChatIdRef.current };
    if (nextValue.trim()) nextDrafts[chatKey] = nextValue;
    else delete nextDrafts[chatKey];
    chatDraftsByChatIdRef.current = nextDrafts;
    setChatDraftsByChatId(nextDrafts);
    saveChatDrafts(nextDrafts);
  }, [newMessage, currentChatId]);

  const [newChatName, setNewChatName] = useState(''); // используется для групп
  const [chatType, setChatType] = useState('group');  // по умолчанию group
  const [selectedParticipants, setSelectedParticipants] = useState([]);

  // для ранговых чатов
  const [rankOptions, setRankOptions] = useState([]); // список уникальных рангов
  const [rankValues, setRankValues] = useState([]);   // выбранные ранги (массив)

  // ▼ отдельный стейт для аватара группового чата (форма создания)
  const [newChatAvatar, setNewChatAvatar] = useState(null);
  const [newChatAvatarPreviewUrl, setNewChatAvatarPreviewUrl] = useState(null);

  // ▼ редактирование аватара группы (✎)
  const [editAvatarChatId, setEditAvatarChatId] = useState(null);
  const editFileRef = useRef(null);

  // ▼ меню «✎»: открыть/закрыть для конкретного чата
  const [editMenuChatId, setEditMenuChatId] = useState(null);

  // ▼ ref контейнера открытого меню для закрытия по клику-вне
  const menuContainerRef = useRef(null);

  const [filter, setFilter] = useState('all'); // 'all' | 'private' | 'group'
  const [isCreateOpen, setCreateOpen] = useState(false);

  // ▼ РЕДАКТИРОВАНИЕ ГРУППОВОГО/РАНГОВОГО ЧАТА
  const [isEditOpen, setEditOpen] = useState(false);
  const [editChatId, setEditChatId] = useState(null);
  const [editChatName, setEditChatName] = useState('');
  const [editParticipants, setEditParticipants] = useState([]); // [{id,name,role,avatar}]
  const [editAvatarFile, setEditAvatarFile] = useState(null);
  const [editAvatarPreviewUrl, setEditAvatarPreviewUrl] = useState(null);
  const editAvatarInputRef = useRef(null);
  // ▼ Меню выбора роли участника (в редактировании чата)
  const [roleMenuFor, setRoleMenuFor] = useState(null);

  // ▼ поиск по чатам (лупа/строка над списком)
  const [showChatSearch, setShowChatSearch] = useState(false);
  const [chatSearch, setChatSearch] = useState('');
  const [debouncedChatSearch, setDebouncedChatSearch] = useState('');
  const [debouncedChatSearchRaw, setDebouncedChatSearchRaw] = useState('');
  const [messageSearchItems, setMessageSearchItems] = useState([]);
  const [messageSearchLoading, setMessageSearchLoading] = useState(false);
  const [messageSearchHasMore, setMessageSearchHasMore] = useState(false);
  const [messageSearchNextOffset, setMessageSearchNextOffset] = useState(null);
  const [messageSearchLoadingMore, setMessageSearchLoadingMore] = useState(false);
  useEffect(() => {
    const t = setTimeout(
      () => {
        const raw = chatSearch.trim();
        setDebouncedChatSearch(raw.toLowerCase());
        setDebouncedChatSearchRaw(raw);
      },
      300
    );
    return () => clearTimeout(t);
  }, [chatSearch]);

  useEffect(() => {
    const q = debouncedChatSearchRaw;
    if (q.length < 2) {
      setMessageSearchItems([]);
      setMessageSearchLoading(false);
      setMessageSearchHasMore(false);
      setMessageSearchNextOffset(null);
      setMessageSearchLoadingMore(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setMessageSearchLoading(true);
        const resp = await apiFetch(
          `${API_URL}/chats/messages/search?query=${encodeURIComponent(q)}&limit=50&offset=0`
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        const items = Array.isArray(data?.items) ? data.items : [];
        if (!cancelled) {
          setMessageSearchItems(items);
          setMessageSearchHasMore(Boolean(data?.has_more));
          setMessageSearchNextOffset(
            Number.isFinite(Number(data?.next_offset))
              ? Number(data.next_offset)
              : null
          );
        }
      } catch (e) {
        console.error('chat message search failed:', e);
        if (!cancelled) {
          setMessageSearchItems([]);
          setMessageSearchHasMore(false);
          setMessageSearchNextOffset(null);
        }
      } finally {
        if (!cancelled) setMessageSearchLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [debouncedChatSearchRaw]);

  const loadMoreMessageSearch = React.useCallback(async () => {
    const q = debouncedChatSearchRaw;
    if (q.length < 2) return;
    if (messageSearchLoading || messageSearchLoadingMore) return;
    if (!messageSearchHasMore || !Number.isFinite(Number(messageSearchNextOffset))) return;

    try {
      setMessageSearchLoadingMore(true);
      const offset = Number(messageSearchNextOffset) || 0;
      const resp = await apiFetch(
        `${API_URL}/chats/messages/search?query=${encodeURIComponent(q)}&limit=50&offset=${offset}`
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      const items = Array.isArray(data?.items) ? data.items : [];

      setMessageSearchItems((prev) => {
        if (!items.length) return prev;
        const seen = new Set(prev.map((it) => `${it.chat_id}:${it.message_id}`));
        const merged = prev.slice();
        for (const it of items) {
          const key = `${it.chat_id}:${it.message_id}`;
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(it);
          }
        }
        return merged;
      });
      setMessageSearchHasMore(Boolean(data?.has_more));
      setMessageSearchNextOffset(
        Number.isFinite(Number(data?.next_offset))
          ? Number(data.next_offset)
          : null
      );
    } catch (e) {
      console.error('chat message search load more failed:', e);
    } finally {
      setMessageSearchLoadingMore(false);
    }
  }, [
    debouncedChatSearchRaw,
    messageSearchHasMore,
    messageSearchLoading,
    messageSearchLoadingMore,
    messageSearchNextOffset,
  ]);


  const socketRef = useRef(null);
  const socketReconnectTimerRef = useRef(null);
  const socketReconnectAttemptRef = useRef(0);
  const REACTION_TOAST_SUPPRESS_MS = 15000;
  const reactionToastSuppressRef = useRef(null);
  const lastReactionTotalByMsgRef = useRef({});
  const messagesEndRef = useRef(null);
  const currentChatIdRef = useRef(currentChatId);
  const chatLoadRequestIdRef = useRef(0);
  const prevChatIdRef = useRef(null); // для сброса непрочитанного при уходе из чата
  const createAvatarInputRef = useRef(null);
  const sidebarColRef = useRef(null);

  //  id сообщения из URL (?m|?msg)
  const [msgFromLink, setMsgFromLink] = useState(null);
  const msgFromLinkRef = useRef(null);
  // Временная подсветка сообщения, к которому докрутили
  const [flashMessageId, setFlashMessageId] = useState(null);
  const [reactionJumpHint, setReactionJumpHint] = useState(null);
  const [unseenReactionMessageIds, setUnseenReactionMessageIds] = useState([]);
  // ВКЛ/ВЫКЛ «умного» автоскролла (вниз только если пользователь у низа)
  const [shouldAutoscroll, setShouldAutoscroll] = useState(true);
  // 🚀 Форс-доскролл только после ОТПРАВКИ своего сообщения
  const [forceScrollOnOwnSend, setForceScrollOnOwnSend] = useState(false);

  //  карта "кто до какого id прочитал" для открытого чата
  const [participantsLastReads, setParticipantsLastReads] = useState({});
  // первое непрочитанное сообщение при открытии чата (для разделителя)
  const [firstUnreadId, setFirstUnreadId] = useState(null);
  // ref для дебounced прогрессивного mark-as-read
  const markReadTimerRef = React.useRef(null);
  const lastMarkedReadIdRef = React.useRef(0);
  // последнее сообщение в открытом чате (для mark-as-read при уходе из чата)
  const lastMessageIdInChatRef = React.useRef(null);
  // при заходе в чат первый скролл вниз делаем мгновенно, чтобы не было дергания
  const initialScrollDoneRef = React.useRef(false);

  React.useEffect(() => {
    if (newMessage && newMessage.trim().length > 0 && firstUnreadId) {
      setFirstUnreadId(null);
    }
  }, [newMessage, firstUnreadId]);

  useEffect(() => {
    setReactionJumpHint(null);
  }, [currentChatId]);

  useEffect(() => {
    if (!reactionJumpHint) return;
    const t = window.setTimeout(() => setReactionJumpHint(null), 45000);
    return () => clearTimeout(t);
  }, [reactionJumpHint]);

  useEffect(() => {
    if (!currentChatId) {
      setUnseenReactionMessageIds([]);
      return;
    }
    const chat = userChatsRef.current.find((c) => String(c.id) === String(currentChatId));
    const rx = chat?.reaction_unread?.messageId;
    setUnseenReactionMessageIds(rx ? [String(rx)] : []);

    setUserChats((p) =>
      p.map((c) =>
        String(c.id) === String(currentChatId) ? { ...c, reaction_unread: null } : c
      )
    );
  }, [currentChatId]);

  useEffect(() => {
    if (!currentChatId || myId == null) return;
    const myIdNum = Number(myId);
    setUnseenReactionMessageIds((prev) => {
      if (!prev.length) return prev;
      const next = prev.filter((id) => {
        const msg = chatMessages.find((m) => String(m.id) === String(id));
        if (!msg) return true;
        if (Number(msg.sender_id) !== myIdNum) return false;
        return sumReactionCounts(msg.reactions) > 0;
      });
      return next.length === prev.length && next.every((id, i) => id === prev[i]) ? prev : next;
    });
  }, [chatMessages, currentChatId, myId]);

  // ====== состояния меню действий / ответ / редактирование / пересылка ======
  const [selectedMessageIds, setSelectedMessageIds] = useState([]); // number[]
  const [selectedMessage, setSelectedMessage] = useState(null); // “primary” (последний клик), нужен для reply/edit при count=1

  const selectedMessages = useMemo(() => {
    const fromChat = chatMessages.filter((m) => selectedMessageIds.includes(m.id));
    if (fromChat.length) return fromChat;
    if (
      selectedMessage
      && selectedMessageIds.length === 1
      && String(selectedMessageIds[0]) === String(selectedMessage.id)
      && (selectedMessage.content || '').trim()
    ) {
      return [selectedMessage];
    }
    return fromChat;
  }, [chatMessages, selectedMessageIds, selectedMessage]);
  const selectedCount = selectedMessageIds.length;

  const selectionHasContent = selectedMessages.some((m) => (m?.content || '').trim())
    || Boolean((selectedMessage?.content || '').trim());
  const deletableCount = selectedMessages.filter(canDeleteMessage).length;

  const [actionBarOpen, setActionBarOpen] = useState(false);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [replyDraft, setReplyDraft] = useState(null); // { id, author, replyTo }
  const [isEditingMessageId, setIsEditingMessageId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [forwardOpen, setForwardOpen] = useState(false);
  const [forwardTargets, setForwardTargets] = useState([]); // список чатов для пересылки (id)
  // 🔹 добавлено: отдельное хранение id пересылаемого сообщения
  const [forwardMessageIds, setForwardMessageIds] = useState([]);
  const [isForwardSelectMode, setIsForwardSelectMode] = useState(false);
  const [forwardSourceChatId, setForwardSourceChatId] = useState(null);
  /** Комментарий к пересылке — уходит отдельным сообщением после forward_bulk */
  const [forwardCommentText, setForwardCommentText] = useState('');
  const forwardSourceMessagesRef = useRef([]);
  // ================================================================================

  //  База для статики бэка (отрезаем /api/vN)
  const API_BASE = (API_URL || '').replace(/\/api\/v\d+\/?$/, '');
  const resolveImageUrl = (path) => {
    if (!path) return null;
    if (/^https?:\/\//i.test(path)) return path;
    const normalized = path.startsWith('/') ? path : `/${path}`;
    return `${API_BASE}${normalized}`;
  };

  const showToast = (message) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ visible: true, message });
    toastTimerRef.current = setTimeout(() => {
      setToast({ visible: false, message: '' });
    }, 4000);
  };
  showToastRef.current = showToast;

  // если пришли ссылкой /chats?open=<ID>&m=<message_id> (или &msg=...) — открыть чат и запомнить сообщение
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const open = params.get("open");
    const msg = params.get("m") || params.get("msg"); // поддерживаем оба варианта

    if (open) {
      const id = Number(open);
      if (!Number.isNaN(id)) {
        setCurrentChatId(prev => (prev !== id ? id : prev));
      }
    } else {
      // Если параметр ?open отсутствует, а чат ещё открыт — закрываем его
      setCurrentChatId(prev => (prev != null ? null : prev));
      // И очищаем возможный «якорь» на сообщение из URL
      setMsgFromLink(prev => (prev != null ? null : prev));
    }

    if (msg) {
      const mid = Number(msg);
      if (!Number.isNaN(mid)) {
        setMsgFromLink(prev => (prev !== mid ? mid : prev));
      }
    }
    // ⛔️ Не очищаем URL здесь — сделаем это после успешного скролла к сообщению
  }, [location.search]);

  useEffect(() => {
    currentChatIdRef.current = currentChatId;
  }, [currentChatId]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !socket.connected) return;

    if (currentChatId) {
      socket.emit("join_chat", { chat_id: Number(currentChatId) });
    } else {
      socket.emit("leave_chat");
    }

    return () => {
      try {
        socket.emit("leave_chat");
      } catch {}
    };
  }, [currentChatId]);

  useEffect(() => {
    msgFromLinkRef.current = msgFromLink ? String(msgFromLink) : null;
  }, [msgFromLink]);

  // При открытии нового чата считаем, что пользователь внизу
  useEffect(() => {
    // ⛔️ ВАЖНО: на старте отключаем автоскролл — сначала хотим якорь на первое непрочитанное.
    if (currentChatId != null) setShouldAutoscroll(false);
  }, [currentChatId])

  useEffect(() => {
    if (!currentChatId) return;

    const body = document.body;
    const html = document.documentElement;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (isIOS) {
      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
      body.style.touchAction = 'none';
      return () => {
        html.style.overflow = '';
        body.style.overflow = '';
        body.style.touchAction = '';
        window.scrollTo(0, 0);
      };
    }

    const scrollY = window.scrollY;
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';

    return () => {
      const top = body.style.top;
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.style.overflow = '';
      const y = parseInt(top || '0', 10) * -1;
      window.scrollTo(0, y);
    };
  }, [currentChatId]);

  useEffect(() => {
    //  регистрируем Web Push по новой схеме
    registerWebPush(/* userId не требуется на бэкенде */);

    // ️ ВАЖНО: не подключаем сокет, пока не знаем myId (idReady && myId)
    if (!idReady || !myId) return;

    if (socketReconnectTimerRef.current) {
      clearTimeout(socketReconnectTimerRef.current);
      socketReconnectTimerRef.current = null;
    }
    socketReconnectAttemptRef.current = 0;

    const socket = io(SOCKET_URL, {
      path: "/ws/socket.io",
      transports: ["websocket"],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 15000,
      timeout: 20000,

      auth: (cb) => {
        cb({
          token: getAccessToken(),
          device_id: getRangiDeviceId(),
        });
      },
    });

    socketRef.current = socket;

    const scheduleManualReconnect = () => {
      if (socketReconnectTimerRef.current || socket.connected) return;
      socketReconnectAttemptRef.current += 1;
      const delay = Math.min(15000, 1000 * (2 ** (socketReconnectAttemptRef.current - 1)));
      socketReconnectTimerRef.current = setTimeout(() => {
        socketReconnectTimerRef.current = null;
        if (socket.connected || socket.active) return;
        try {
          socket.connect();
        } catch {
          scheduleManualReconnect();
        }
      }, delay);
    };

    socket.on("connect", () => {
      console.log("🔌 Socket.IO подключен");
      socketReconnectAttemptRef.current = 0;
      if (socketReconnectTimerRef.current) {
        clearTimeout(socketReconnectTimerRef.current);
        socketReconnectTimerRef.current = null;
      }

      if (currentChatIdRef.current) {
        socket.emit("join_chat", { chat_id: Number(currentChatIdRef.current) });
      }
    });

    socket.on("connect_error", (err) => {
      const message = String(err?.message || err || '');

      console.warn("⚠️ Socket.IO connect_error:", message);

      const isLicenseError =
        message.includes("DEVICE_LIMIT_EXCEEDED") ||
        message.toLowerCase().includes("license") ||
        message.includes("missing device_id") ||
        message.includes("not authorized");

      if (isLicenseError) {
        showToastRef.current?.(
          "Подключение ограничено лицензией. Превышен лимит активных устройств или лицензия недоступна."
        );
      }

      scheduleManualReconnect();
    });

    // ✅ вариант 1 (рекомендуемый): снапшот + инкрементальные апдейты
    socket.on("presence_snapshot", (payload) => {
      const list = payload?.online_employee_ids || payload?.online || [];
      if (!Array.isArray(list)) return;
      const next = {};
      list.forEach((id) => {
        const n = Number(id);
        if (Number.isFinite(n)) next[n] = true;
      });
      setOnlineByEmployeeId(next);
    });

    socket.on("presence_update", (payload) => {
      const id = payload?.employee_id ?? payload?.id;
      const online =
        payload?.online ??
        (payload?.status ? String(payload.status).toLowerCase() === 'online' : undefined);

      if (id == null || online == null) return;
      setEmployeeOnline(id, online);
    });

    // ✅ “на всякий случай” поддержка альтернативных имён событий
    socket.on("employee_online", (payload) => setEmployeeOnline(payload?.employee_id ?? payload, true));
    socket.on("employee_offline", (payload) => setEmployeeOnline(payload?.employee_id ?? payload, false));


    // === realtime: новое сообщение ===
    socket.on("new_message", async (data) => {
      const msg = data?.message;
      if (!msg) return;

      const myIdNum = Number(myId);
      // ⚠️ Игнорируем свои сообщения ТОЛЬКО когда это текущий открытый чат (чтобы не было дубля с POST).
      // Для других чатов — обрабатываем, чтобы превью обновлялось у автора пересылки.
      if (
        Number.isFinite(myIdNum) &&
        Number(msg.sender_id) === myIdNum &&
        String(data.chat_id) === String(currentChatIdRef.current)
      ) {
        return;
      }

      if (data.chat_id === currentChatIdRef.current) {
        if (messagesHasMoreNewerRef.current) {
          const currentWindow = chatMessagesRef.current || [];
          const lastWindowMessage = currentWindow.length ? currentWindow[currentWindow.length - 1] : null;
          if (lastWindowMessage?.id != null) {
            setMessagesAfterCursor(lastWindowMessage.id);
          }
          setMessagesHasMoreNewer(true);
        } else {
          setChatMessages(prev => {
            const seen = new Set(prev.map((m) => String(m.id)));
            if (seen.has(String(data.message.id))) return prev;
            const trimmed = trimChatWindowFromStart([
              ...prev,
              normalizeChatMessage(data.message),
            ]);
            if (trimmed.trimmed && trimmed.cursor != null) {
              setMessagesHasMore(true);
              setMessagesCursor(trimmed.cursor);
              setMessagesAroundRevision((v) => v + 1);
            }
            return trimmed.messages;
          });
        }
        if (data.message?.id) lastMessageIdInChatRef.current = data.message.id;

        // СИНХРОНИЗАЦИЯ превью текущего чата
        const hasAtt = Array.isArray(data.message?.attachments) && data.message.attachments.length > 0;
        const isFwd = !!data.message?.forwarded_from_message_id;
        const fwdFrom = data.message?.forwarded_from_sender_name;
        const base = (data.message?.content ?? '');
        const text = isFwd && fwdFrom
          ? `Переслано от ${fwdFrom}: ${base || (hasAtt ? '📎 вложение' : '')}`
          : (base || (hasAtt ? '📎 вложение' : ''));

        setUserChats(prev =>
          prev.map(c => c.id === data.chat_id ? {
            ...c,
            last_message_text: text,
            last_message_time: data.message?.timestamp || c.last_message_time,
            // плашка непрочитанного остаётся до ответа или ухода из чата — не обнуляем
          } : c)
        );

      } else {
        // обновим превью списка чатов (последний текст/время и +1 unread)
        setUserChats(prev => {
          const iso = data.message?.timestamp;
          const id = data.chat_id;
          // 🔹 Поддержка пометки "Переслано от ..."
          const hasAtt = Array.isArray(data.message?.attachments) && data.message.attachments.length > 0;
          const isFwd = !!data.message?.forwarded_from_message_id;
          const fwdFrom = data.message?.forwarded_from_sender_name;
          const base = (data.message?.content ?? '');
          const text = isFwd && fwdFrom
            ? `Переслано от ${fwdFrom}: ${base || (hasAtt ? '📎 вложение' : '')}`
            : (base || (hasAtt ? '📎 вложение' : ''));

          //  не увеличиваем счетчик, если сообщение своё
          const isOwn = Number.isFinite(myIdNum) && Number(msg.sender_id) === myIdNum;

          const updated = prev.map(c => c.id === id ? {
            ...c,
            last_message_text: text,
            last_message_time: iso || c.last_message_time,
            unread_count: isOwn ? (c.unread_count || 0) : (c.unread_count || 0) + 1,
          } : c);
          return updated;
        });
        // обновляем бейдж в форграунде, если это НЕ моё сообщение
        const isOwn = Number.isFinite(myIdNum) && Number(msg.sender_id) === myIdNum;
        if (!isOwn) syncBadge();
      }
    });

    // событие обновления сайдбара //
    socket.on("chat_sidebar_update", (data) => {
      if (!data?.chat_id) return;

      const chatId = data.chat_id;
      const isCurrentChat = String(chatId) === String(currentChatIdRef.current);

      // Если чат открыт, его обновляет new_message из chat:{chat_id}
      if (isCurrentChat) return;

      const myIdNum = Number(myId);
      const isOwn = Number.isFinite(myIdNum) && Number(data.sender_id) === myIdNum;

      setUserChats((prev) =>
        prev.map((c) =>
          String(c.id) === String(chatId)
            ? {
                ...c,
                last_message_text: data.last_message_text || (data.has_attachments ? '📎 вложение' : ''),
                last_message_time: data.last_message_time || c.last_message_time,
                unread_count:
                 Number(data.sender_id) === Number(myId)
                   ? (Number(c.unread_count) || 0)
                   : (Number(c.unread_count) || 0) + 1,
              }
            : c
        )
      );

      if (!isOwn) syncBadge();
    });

    // === realtime: создан новый чат ===
    socket.on("chat_created", (payload) => {
      try {
        if (!payload || !Array.isArray(payload.participant_ids)) return;
        // показываем только тем, кто действительно участник
        if (!myId || !payload.participant_ids.includes(Number(myId))) return;

        setUserChats(prev => {
          // уже есть реальный чат с этим id → ничего не делаем
          if (prev.some(c => String(c.id) === String(payload.id))) return prev;

          const payloadPeerId = Array.isArray(payload.participant_ids)
            ? payload.participant_ids.map(Number).find((id) => String(id) !== String(myId))
            : null;

          // если есть ВИРТУАЛЬНАЯ карточка этого собеседника — заменим её
          const idxVirtual = prev.findIndex(c =>
            c.__virtual &&
            Number.isFinite(payloadPeerId) &&
            String(c.__targetEmployeeId) === String(payloadPeerId)
          );

          const next = prev.slice();
          const preview = {
            id: payload.id,
            name: payload.name,
            type: normalizeType(payload.type), // нормализуем тип
            last_message_text: null,
            last_message_time: null,
            unread_count: 0,
            is_muted: Boolean(payload?.is_muted),
            // если payload.avatar_url пуст — пробуем взять из виртуальной карточки
            avatar_url: (idxVirtual >= 0 ? prev[idxVirtual].avatar_url : null) || payload.avatar_url || null,
            participant_ids: Array.isArray(payload.participant_ids) ? payload.participant_ids : undefined,
          };

          if (idxVirtual >= 0) {
            next[idxVirtual] = preview; // заменить виртуал на реальный
            return next;
          }

          // иначе добавляем новый превью в начало
          return [preview, ...next];
        });
      } catch {
        /* ignore */
      }
    });

    // === realtime: обновлён аватар чата (group ИЛИ private) ===
    socket.on("chat_avatar_updated", ({ chat_id, avatar_url }) => {
      setUserChats(prev =>
        prev.map(c => String(c.id) === String(chat_id) ? { ...c, avatar_url: avatar_url || null } : c)
      );
    });

    // === realtime: чат удалён (кем-то) ===
    socket.on("chat_deleted", ({ chat_id }) => {
      setUserChats(prev => prev.filter(c => String(c.id) !== String(chat_id)));
      if (String(currentChatIdRef.current) === String(chat_id)) {
        setCurrentChatId(null);
        setChatMessages([]);
      }
    });

    // === realtime: чат обновлён (имя/аватар/состав)
    socket.on("chat_updated", async (data) => {
      const id = data?.chat_id;
      if (!id) return;

      const ids = Array.isArray(data?.participant_ids) ? data.participant_ids.map(Number) : null;
      const myIdNum = Number(myId);

      // 1) Если пришёл новый состав и меня там НЕТ — убрать чат немедленно
      if (ids && Number.isFinite(myIdNum) && !ids.includes(myIdNum)) {
        setUserChats(prev => prev.filter(c => String(c.id) !== String(id)));
        if (String(currentChatIdRef.current) === String(id)) {
          setCurrentChatId(null);
          setChatMessages([]);
          // сбросить параметр ?open, чтобы не оставаться на недоступном чате
          navigate('/chats');
        }
        return;
      }

      // 2) Если пришёл новый состав и меня туда ДОБАВИЛИ — добавить чат, если его ещё нет
      if (ids && Number.isFinite(myIdNum) && ids.includes(myIdNum)) {
        let needInsert = false;
        setUserChats(prev => {
          const exists = prev.some(c => String(c.id) === String(id));
          needInsert = !exists;
          // если уже есть — просто обновим имя/аватар ниже отдельным set-ом
          return exists ? prev : [
            {
              id,
              name: data.name ?? 'Новый чат',
              avatar_url: data.avatar_url ?? null,
              unread_count: 0,
              is_muted: Boolean(data?.is_muted),
              last_message_text: '',
              last_message_time: Date.now(),
              type: 'group', // или 'rank' — точный тип можно подтянуть запросом ниже
            },
            ...prev
          ];
        });

        // Опционально: подтянуть точное превью с сервера и обновить карточку
        if (needInsert) {
          try {
            const resp = await apiFetch(`${API_URL}/chats/${id}`);
            if (resp.ok) {
              const payload = await resp.json();
              setUserChats(prev => prev.map(c =>
                String(c.id) === String(id)
                  ? {
                    ...c,
                    name: data.name != null ? data.name : (payload?.name ?? c.name),
                    avatar_url: data.avatar_url != null ? data.avatar_url : (payload?.avatar_url ?? c.avatar_url),
                    is_muted: payload?.is_muted != null ? Boolean(payload.is_muted) : c.is_muted,

                    // ✅ сохраняем состав, если сервер прислал
                    participant_ids: Array.isArray(data.participant_ids)
                      ? data.participant_ids.map(Number)
                      : c.participant_ids,
                  }
                  : c
              ));
            }
          } catch { /* ignore best-effort */ }
        }
      }

      // 3) В любом случае — синхронизируем имя/аватар для существующей карточки
      setUserChats(prev => prev.map(c =>
        String(c.id) === String(id)
          ? {
            ...c,
            name: data.name != null ? data.name : c.name,
            avatar_url: data.avatar_url != null ? data.avatar_url : c.avatar_url,
            is_muted: data?.is_muted != null ? Boolean(data.is_muted) : c.is_muted,
          }
          : c
      ));
    });


    // === realtime: обновление прочитанного ===
    socket.on("chat_read", ({ chat_id, employee_id, last_read_message_id }) => {
      if (!chat_id || employee_id == null) return;

      if (String(employee_id) === String(myId)) {
        setUserChats(prev =>
          prev.map(c =>
            String(c.id) === String(chat_id)
              ? { ...c, unread_count: 0 }
              : c
          )
        );
        syncBadge();
      }

      if (String(chat_id) === String(currentChatIdRef.current)) {
        setParticipantsLastReads(prev => ({
          ...prev,
          [employee_id]: last_read_message_id
        }));
      }
    });

    // === реакции на сообщении (realtime) ===
    socket.on("message_reactions_updated", ({ chat_id, message_id, reactions }) => {
      if (message_id == null || chat_id == null) return;
      const nextReactions = Array.isArray(reactions) ? reactions : [];
      // В socket broadcast поле reacted_by_me не персонализировано по получателю.
      // Поэтому до персонального refresh не подсвечиваем «мои» реакции по этим данным.
      const nextReactionsSafe = nextReactions.map((r) => ({ ...r, reacted_by_me: false }));
      const nextTotal = sumReactionCounts(nextReactions);
      const totalKey = `${chat_id}:${message_id}`;
      const isCurrent = String(chat_id) === String(currentChatIdRef.current);

      if (isCurrent) {
        setChatMessages((prev) => {
          lastReactionTotalByMsgRef.current[totalKey] = nextTotal;
          const msg = prev.find((m) => String(m.id) === String(message_id));
          if (
            msg &&
            myId != null &&
            String(msg.sender_id) === String(myId)
          ) {
            const prevReactions = Array.isArray(msg.reactions) ? msg.reactions : [];
            const prevTotal = sumReactionCounts(prevReactions);
            if (nextTotal > prevTotal) {
              const sk = reactionToastSuppressRef.current;
              const skip =
                sk &&
                String(sk.messageId) === String(message_id) &&
                Date.now() - sk.at < REACTION_TOAST_SUPPRESS_MS;
              if (!skip) {
                const emoji = findPrimaryNewReactionEmoji(prevReactions, nextReactions);
                queueMicrotask(() => {
                  setReactionJumpHint({
                    messageId: String(message_id),
                    emoji: emoji || null,
                  });
                  setUnseenReactionMessageIds((prev) => {
                    const s = String(message_id);
                    return prev.includes(s) ? prev : [...prev, s];
                  });
                });
              } else if (sk && String(sk.messageId) === String(message_id)) {
                // Гасим только echo нашего собственного действия; следующие события по сообщению не подавляем.
                reactionToastSuppressRef.current = null;
              }
            }
          }
          return prev.map((m) =>
            String(m.id) === String(message_id)
              ? { ...m, reactions: nextReactionsSafe }
              : m
          );
        });

        void (async () => {
          try {
            const res = await apiFetch(
              `${API_URL}/chats/${chat_id}/messages/around?message_id=${message_id}&before=0&after=0`
            );
            if (!res.ok) return;
            const data = await res.json();
            const msgs = Array.isArray(data?.messages) ? data.messages : [];
            const anchor = msgs.find((m) => String(m.id) === String(message_id));
            if (!anchor) return;
            const personalReactions = Array.isArray(anchor.reactions) ? anchor.reactions : [];
            setChatMessages((prev) => prev.map((m) =>
              String(m.id) === String(message_id)
                ? { ...m, reactions: personalReactions }
                : m
            ));
          } catch { /* ignore */ }
        })();
        return;
      }

      const prevStored = lastReactionTotalByMsgRef.current[totalKey];
      if (nextTotal <= 0) {
        lastReactionTotalByMsgRef.current[totalKey] = 0;
        return;
      }
      if (prevStored != null && nextTotal <= prevStored) {
        lastReactionTotalByMsgRef.current[totalKey] = nextTotal;
        return;
      }
      lastReactionTotalByMsgRef.current[totalKey] = nextTotal;

      void (async () => {
        try {
          const res = await apiFetch(
            `${API_URL}/chats/${chat_id}/messages/around?message_id=${message_id}&before=0&after=0`
          );
          if (!res.ok) return;
          const data = await res.json();
          const msgs = Array.isArray(data?.messages) ? data.messages : [];
          const anchor = msgs.find((m) => String(m.id) === String(message_id));
          if (!anchor || myId == null || String(anchor.sender_id) !== String(myId)) return;

          const emoji = pickDominantReactionEmoji(nextReactions);

          queueMicrotask(() =>
            setUserChats((prev) =>
              prev.map((c) =>
                String(c.id) === String(chat_id)
                  ? {
                    ...c,
                    reaction_unread: {
                      messageId: String(message_id),
                      emoji: emoji || null,
                    },
                  }
                  : c
              )
            )
          );
        } catch { /* ignore */ }
      })();
    });

    // === сообщение обновлено (редактирование) ===
    socket.on("message_updated", ({ chat_id, message }) => {
      if (!message) return;
      if (String(chat_id) === String(currentChatIdRef.current)) {
        setChatMessages(prev => prev.map(m => {
          if (m.id !== message.id) return m;
          const merged = { ...m, ...message };
          if (!Array.isArray(message.reactions)) {
            merged.reactions = Array.isArray(m.reactions) ? m.reactions : [];
          }
          return merged;
        }));

        // СИНХРОНИЗАЦИЯ превью текущего чата
        const hasAtt = Array.isArray(message?.attachments) && message.attachments.length > 0;
        const isFwd = !!message?.forwarded_from_message_id;
        const fwdFrom = message?.forwarded_from_sender_name;
        const base = (message?.content ?? '');
        const text = isFwd && fwdFrom
          ? `Переслано от ${fwdFrom}: ${base || (hasAtt ? '📎 вложение' : '')}`
          : (base || (hasAtt ? '📎 вложение' : ''));

        setUserChats(prev => prev.map(c => c.id === chat_id ? {
          ...c,
          last_message_text: text,
          last_message_time: message.timestamp || c.last_message_time
        } : c));
      } else {
        // если это последнее в другом чате — можно освежить превью
        setUserChats(prev => prev.map(c => {
          if (c.id !== chat_id) return c;
          // держим логику префикса для пересланных и тут
          const hasAtt = Array.isArray(message?.attachments) && message.attachments.length > 0;
          const isFwd = !!message?.forwarded_from_message_id;
          const fwdFrom = message?.forwarded_from_sender_name;
          const base = (message?.content ?? '');
          const text = isFwd && fwdFrom
            ? `Переслано от ${fwdFrom}: ${base || (hasAtt ? '📎 вложение' : '')}`
            : (base || (hasAtt ? '📎 вложение' : ''));

          return {
            ...c,
            last_message_text: text,
            last_message_time: message.timestamp || c.last_message_time
          };
        }));
      }
    });

    socket.on("chat_pinned_messages_updated", (payload) => {
      const cid = payload?.chat_id ?? payload?.chatId;
      if (cid == null) return;
      if (String(cid) !== String(currentChatIdRef.current)) return;
      const items = Array.isArray(payload?.pinned_messages)
        ? payload.pinned_messages
        : Array.isArray(payload?.items)
          ? payload.items
          : null;
      if (items != null) {
        setPinnedMessagesItems(items);
        setPinnedMetaReady(true);
      } else {
        void refreshPinnedMessages(cid).then(() => {
          setPinnedMetaReady(true);
        });
      }
    });

    // === сообщение удалено: ПОЛНОСТЬЮ убирать из стейта ===
    socket.on("message_deleted", ({ chat_id, message_id }) => {
      // работаем только если удаление относится к открытому сейчас чату
      if (String(chat_id) !== String(currentChatIdRef.current)) return;

      setChatMessages(prev => {
        const next = prev.filter(m => m.id !== message_id);
        const last = next.length ? next[next.length - 1] : null;

        setUserChats(prevChats =>
          prevChats.map(c => c.id === chat_id ? {
            ...c,
            last_message_text: last
              ? (() => {
                const hasAtt = Array.isArray(last.attachments) && last.attachments.length > 0;
                const base = (last.content ?? '');
                const text = base || (hasAtt ? '📎 вложение' : '');
                if (last.forwarded_from_message_id && last.forwarded_from_sender_name) {
                  return `Переслано от ${last.forwarded_from_sender_name}: ${text}`;
                }
                return text;
              })()
              : null,
            last_message_time: last ? (last.timestamp || null) : null
          } : c)
        );

        return next;
      });

      // сбрасываем ответ, если ссылались на удалённое
      setReplyDraft(prev => (prev?.id === message_id ? null : prev));

      // если удалили "primary" — сбросить
      setSelectedMessage(prev => (prev?.id === message_id ? null : prev));

      // убрать из мультивыбора; панель закрыть только если выбор опустел
      setSelectedMessageIds(prev => {
        const next = prev.filter(id => id !== message_id);
        if (next.length === 0) setActionBarOpen(false);
        return next;
      });
      setPinnedMessagesItems((prev) => prev.filter((p) => String(p.id) !== String(message_id)));
    });

    socket.on("disconnect", () => {
      console.log("❌ Socket.IO отключен");
      scheduleManualReconnect();
    });

    return () => {
      if (socketReconnectTimerRef.current) {
        clearTimeout(socketReconnectTimerRef.current);
        socketReconnectTimerRef.current = null;
      }
      try {
        socket.emit("leave_chat");
      } catch {}

      socket.disconnect();
      socketRef.current = null;
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, [idReady, myId, navigate, setEmployeeOnline, refreshPinnedMessages]);

  useEffect(() => {
    const fetchUserChats = async () => {
      try {
        setLoading(true);
        setError(null);

        // 1) реальные чаты
        const response = await apiFetch(`${API_URL}/chats/`);
        if (!response.ok) throw new Error(`Ошибка сети или сервера: ${response.status}`);
        const realChats = await response.json();
        const normalizedRealChats = (Array.isArray(realChats) ? realChats : []).map((chat) => ({
          ...chat,
          is_muted: Boolean(chat?.is_muted),
        }));

        // 2) возможные собеседники для личек
        const targets = await fetchPossibleTargets(myId, role);

        // Уже существующие личные чаты сопоставляем по ID собеседника.
        // Имя не подходит: два сотрудника могут быть тёзками.
        const existingPrivatePeerIds = new Set(
          normalizedRealChats
            .filter(c => normalizeType(c.type) === 'private')
            .map(getPeerEmployeeId)
            .filter((peerId) => peerId != null)
            .map(String)
        );

        // 3) виртуальные карточки для отсутствующих личек
        const virtualPrivates = targets
          .filter(t => !existingPrivatePeerIds.has(String(t.id)))
          .map(t => ({
            id: `v-${t.id}`,            // виртуальный ключ
            name: t.name,
            type: 'private',
            last_message_text: null,    // → «Сообщений пока нет»
            last_message_time: null,
            unread_count: 0,
            is_muted: false,
            avatar_url: t.avatar || null,
            __virtual: true,
            __targetEmployeeId: t.id
          }));

        // 4) объединяем: виртуальные лички + реальные чаты
        setUserChats([...virtualPrivates, ...normalizedRealChats]);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    // ⛔️ Не грузим список чатов до инициализации employee_id
    if (!idReady || !myId) return;
    fetchUserChats();
  }, [idReady, myId, role, getPeerEmployeeId]);

  useEffect(() => {
    let isEffectActive = true;
    const requestId = ++chatLoadRequestIdRef.current;
    const isRequestStale = () =>
      !isEffectActive ||
      chatLoadRequestIdRef.current !== requestId ||
      String(currentChatIdRef.current ?? '') !== String(currentChatId ?? '');

    // При уходе из чата — помечаем его прочитанным и снимаем плашку непрочитанного
    const prevId = prevChatIdRef.current;
    const lastMsgId = lastMessageIdInChatRef.current;
    if (prevId != null && String(prevId) !== String(currentChatId)) {
      const leavingChatId = prevId;
      setUserChats(p => p.map(c => c.id === leavingChatId ? { ...c, unread_count: 0, reaction_unread: null } : c));
      syncBadge();
      if (lastMsgId != null) {
        apiFetch(`${API_URL}/chats/${leavingChatId}/read`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ up_to_message_id: lastMsgId }),
        }).catch(() => {});
      }
    }
    prevChatIdRef.current = currentChatId;
    lastMessageIdInChatRef.current = null;

    if (currentChatId) {
      initialScrollDoneRef.current = false;
      prependScrollRestoreRef.current = null;
      historyPrependScrollLockRef.current = false;
      historyAppendScrollLockRef.current = false;
      loadingOlderInFlightRef.current = false;
      loadingNewerInFlightRef.current = false;
      const fetchChatMessages = async () => {
        try {
          // Мгновенно очищаем ленту при смене чата, чтобы не показывать сообщения прошлого диалога.
          setChatMessages([]);
          setParticipantsLastReads({});
          setFirstUnreadId(null);
          setIsChatSwitching(true);
          setLoading(true);
          setError(null);
          // сброс пагинации при открытии нового чата
          setMessagesHasMore(false);
          setMessagesCursor(null);
          setLoadingOlder(false);
          setPinnedMessagesItems([]);
          setPinnedMetaReady(false);
          const urlParams = new URLSearchParams(window.location.search);
          const urlAnchor = urlParams.get('m') || urlParams.get('msg');
          const urlOpen = urlParams.get('open');
          const keepUrlAnchor = urlAnchor && String(urlOpen) === String(currentChatId);
          if (!keepUrlAnchor) {
            setMsgFromLink(null);
          }
          const response = await apiFetch(`${API_URL}/chats/${currentChatId}/messages?limit=${CHAT_MESSAGES_PAGE_SIZE}`);
          if (isRequestStale()) return;
          if (response.status === 403) {
            // Не участник чата: молча закрываем экран и показываем тост.
            showToast('Вы не участник этого чата');
            setCurrentChatId(null);
            return;
          }
          if (!response.ok) {
            throw new Error(`Ошибка сети или сервера: ${response.status}`);
          }
          const data = await response.json();
          if (isRequestStale()) return;

          // Новый формат ответа: { messages: [...], participants_last_reads: {...} }
          const messages = Array.isArray(data?.messages) ? data.messages : (Array.isArray(data) ? data : []);
          setChatMessages(messages.map((m) => normalizeChatMessage(m)));
          const embFromMessages = extractPinnedListFromEnvelope(data);
          if (embFromMessages != null) setPinnedMessagesItems(embFromMessages);
          lastMessageIdInChatRef.current = messages.length ? messages[messages.length - 1].id : null;
          const msgs = Array.isArray(data?.messages) ? data.messages : [];
          if (msgs.length) {
            setMessagesAfterCursor(msgs[msgs.length - 1].id);
          }
          setMessagesHasMoreNewer(false);

          setMessagesHasMore(!!data?.has_more);
          setMessagesCursor(data?.next_before_id ?? null);

          const lastReads = data?.participants_last_reads || {};
          setParticipantsLastReads(lastReads);

          const requestedAnchorId = Number(msgFromLinkRef.current || 0);
          const hasRequestedAnchorInBatch = Number.isFinite(requestedAnchorId) && requestedAnchorId > 0
            ? messages.some((m) => Number(m?.id) === requestedAnchorId)
            : false;

          if (Number.isFinite(requestedAnchorId) && requestedAnchorId > 0 && !hasRequestedAnchorInBatch) {
            try {
              console.log('[autoscroll] loading around anchor', requestedAnchorId, 'chat', currentChatId);
              const aroundResp = await apiFetch(
                `${API_URL}/chats/${currentChatId}/messages/around?message_id=${requestedAnchorId}&before=25&after=25`
              );
              if (isRequestStale()) return;
              if (aroundResp.ok) {
                const aroundData = await aroundResp.json();
                if (isRequestStale()) return;
                const aroundMessages = Array.isArray(aroundData?.messages) ? aroundData.messages : [];
                if (aroundMessages.length) {
                  setChatMessages(aroundMessages.map((m) => normalizeChatMessage(m)));
                  const embAround = extractPinnedListFromEnvelope(aroundData);
                  if (embAround != null) setPinnedMessagesItems(embAround);
                  setMessagesHasMore(!!aroundData?.has_more_older);
                  setMessagesHasMoreNewer(!!aroundData?.has_more_newer);
                  const firstId = aroundMessages[0]?.id ?? null;
                  const lastId = aroundMessages[aroundMessages.length - 1]?.id ?? null;
                  setMessagesCursor(firstId);
                  setMessagesAfterCursor(lastId);
                  lastMessageIdInChatRef.current = lastId;
                  setShouldAutoscroll(false);
                }
              } else {
                console.warn('[autoscroll] around API error', aroundResp.status, 'for anchor', requestedAnchorId, '— clearing anchor, restoring autoscroll');
                setMsgFromLink(null);
                setShouldAutoscroll(true);
              }
            } catch (e) {
              console.error('[autoscroll] load around for anchor failed:', e, '— clearing anchor');
              setMsgFromLink(null);
              setShouldAutoscroll(true);
            }
          }

          try {
            if (!isRequestStale()) {
              const pr = await apiFetch(`${API_URL}/chats/${currentChatId}/pinned-messages`);
              if (!isRequestStale() && pr.ok) {
                const pd = await pr.json();
                const pinItems = Array.isArray(pd?.items) ? pd.items : Array.isArray(pd) ? pd : [];
                setPinnedMessagesItems(pinItems);
              }
            }
          } catch {
            /* ignore */
          }

          // ▼ Определяем первый непрочитанный для текущего пользователя
          const myIdNum = Number(myId);
          const myLastRead = Number(
            myIdNum && lastReads && Object.prototype.hasOwnProperty.call(lastReads, myIdNum)
              ? lastReads[myIdNum]
              : -1
          );
          lastMarkedReadIdRef.current = myLastRead >= 0 ? myLastRead : 0;
          // первый message.id строго больше моего last_read
          const firstUnread = messages.find(m => Number(m?.id) > myLastRead)?.id;
          setFirstUnreadId(firstUnread ?? null);
          if (!keepUrlAnchor) {
            if (firstUnread != null) {
              console.log('[autoscroll] setting msgFromLink to firstUnread:', firstUnread, 'chat:', currentChatId);
              setMsgFromLink(String(firstUnread));
            } else {
              setShouldAutoscroll(true);
            }
          } else if (!msgFromLinkRef.current && firstUnread != null) {
            setMsgFromLink(String(firstUnread));
          } else if (!msgFromLinkRef.current) {
            setShouldAutoscroll(true);
          }

          //️ СИНХРОНИЗАЦИЯ превью для текущего чата по факту ответа
          if (messages.length) {
            const last = messages[messages.length - 1];
            const hasAtt = Array.isArray(last?.attachments) && last.attachments.length > 0;
            const isFwd = !!last?.forwarded_from_message_id;
            const fwdFrom = last?.forwarded_from_sender_name;
            const base = (last?.content ?? '');
            const text = isFwd && fwdFrom
              ? `Переслано от ${fwdFrom}: ${base || (hasAtt ? '📎 вложение' : '')}`
              : (base || (hasAtt ? '📎 вложение' : ''));

            setUserChats(prev =>
              prev.map(c => c.id === currentChatId ? {
                ...c,
                last_message_text: text,
                last_message_time: last.timestamp || c.last_message_time,
              } : c)
            );
          } else {
            // Пустой чат: очистим превью
            setUserChats(prev =>
              prev.map(c => c.id === currentChatId ? {
                ...c,
                last_message_text: null,
                last_message_time: null,
              } : c)
            );
          }

          // Если непрочитанных нет — сразу помечаем прочитанным до конца
          // Если есть — mark-as-read будет вызываться прогрессивно по скроллу
          const hasUnread = messages.some(m => Number(m?.id) > myLastRead);
          if (!hasUnread && messages.length) {
            try {
              const lastId = messages[messages.length - 1].id;
              lastMarkedReadIdRef.current = Math.max(lastMarkedReadIdRef.current, Number(lastId));
              await apiFetch(`${API_URL}/chats/${currentChatId}/read`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ up_to_message_id: lastId }),
              });
              setUserChats(prev =>
                prev.map(c => c.id === currentChatId ? { ...c, unread_count: 0 } : c)
              );
              await syncBadge();
            } catch { /* ignore */ }
          }
        } catch (err) {
          if (isRequestStale()) return;
          setError(err.message);
        } finally {
          if (!isRequestStale()) {
            setLoading(false);
            setIsChatSwitching(false);
            setPinnedMetaReady(true);
          }
        }
      };

      fetchChatMessages();
    } else {
      setChatMessages([]);
      setParticipantsLastReads({});
      setPinnedMessagesItems([]);
      setPinnedMetaReady(false);
      setIsChatSwitching(false);
    }

    return () => {
      isEffectActive = false;
    };
  }, [currentChatId]);

  const scrollMessagesContainerToBottom = React.useCallback((behavior = 'auto') => {
    const container = messagesContainerRef.current;
    if (!container?.isConnected) return;
    const top = Math.max(0, container.scrollHeight - container.clientHeight);
    try {
      container.scrollTo({ top, behavior });
    } catch {
      container.scrollTop = top;
    }
  }, []);

  // Выполняем скролл только после того, как DOM «устаканился»:
  // двойной rAF + небольшой таймаут как страховка.
  const scrollToBottomAfterLayout = React.useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollMessagesContainerToBottom('smooth');
        setTimeout(() => {
          scrollMessagesContainerToBottom('auto');
        }, 60);
      });
    });
  }, [scrollMessagesContainerToBottom]);

  const requestLiveBottom = React.useCallback(() => {
    setMsgFromLink(null);
    setShouldAutoscroll(true);
  }, []);

  useLayoutEffect(() => {
    if (!currentChatId) return;
    const el = messagesContainerRef.current;
    if (el?.isConnected) {
      el.scrollTop = 0;
    }
  }, [currentChatId]);

  // useLayoutEffect: скролл до отрисовки, чтобы при заходе в чат не показывало верх и не дергало
  useLayoutEffect(() => {
    if (isSearchMode) return;
    if (msgFromLink) return;
    if (loadingOlder) return;
    if (historyPrependScrollLockRef.current) return;
    if (historyAppendScrollLockRef.current) return;
    if (forceScrollOnOwnSend) {
      setForceScrollOnOwnSend(false);
      setShouldAutoscroll(true);
      scrollToBottomAfterLayout();
      return;
    }
    if (!shouldAutoscroll) return;
    if (!Array.isArray(chatMessages) || !chatMessages.length) return;
    const scrollBox = messagesContainerRef.current;
    if (scrollBox?.isConnected && !forceScrollOnOwnSend) {
      const diff = Math.max(0, scrollBox.scrollHeight - scrollBox.scrollTop - scrollBox.clientHeight);
      const nearBottom = diff < CHAT_NEAR_BOTTOM_PX || scrollBox.scrollHeight <= scrollBox.clientHeight + 1;
      if (!nearBottom) return;
    }
    if (!initialScrollDoneRef.current) {
      scrollMessagesContainerToBottom('auto');
      initialScrollDoneRef.current = true;
      return;
    }
    scrollToBottomAfterLayout();
  }, [
    chatMessages.length,
    messagesAroundRevision,
    msgFromLink,
    shouldAutoscroll,
    forceScrollOnOwnSend,
    scrollToBottomAfterLayout,
    scrollMessagesContainerToBottom,
    isSearchMode,
    loadingOlder,
  ]);

  // Колбэк, сообщающий, близко ли пользователь к низу списка сообщений
  const handleMessagesScroll = React.useCallback((isAtBottom) => {
    const atLiveBottom = isAtBottom && !messagesHasMoreNewer;
    setShouldAutoscroll(prev => {
      if (prev !== atLiveBottom) {
        console.log('[autoscroll] shouldAutoscroll changed:', prev, '->', atLiveBottom);
      }
      return prev === atLiveBottom ? prev : atLiveBottom;
    });

    if (!currentChatId) return;

    // Прогрессивный mark-as-read: находим последнее видимое сообщение в контейнере.
    // Важно: не откатываем позицию «прочитано» при скролле вверх (например по ответу) —
    // отмечаем только если пользователь дочитал до более нового сообщения.
    const doMark = () => {
      const el = messagesContainerRef.current;
      if (!el) return;

      const containerBottom = el.getBoundingClientRect().bottom;
      const msgEls = el.querySelectorAll('[data-msg-id]');
      let lastVisibleId = null;
      for (const msgEl of msgEls) {
        const rect = msgEl.getBoundingClientRect();
        if (rect.top < containerBottom) {
          const id = Number(msgEl.dataset.msgId);
          if (!isNaN(id)) lastVisibleId = id;
        }
      }
      if (!lastVisibleId) return;

      const prevMarked = Number(lastMarkedReadIdRef.current || 0);

      if (lastVisibleId <= prevMarked) {
        return;
      }

      lastMarkedReadIdRef.current = lastVisibleId;

      // Отправляем read на сервер (для синхронизации), но плашку непрочитанного не трогаем — она снимается только при ответе или уходе из чата
      apiFetch(`${API_URL}/chats/${currentChatId}/read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ up_to_message_id: lastVisibleId }),
      }).catch(() => { });
    };

    // Дебounce 600ms чтобы не спамить API при быстром скролле
    clearTimeout(markReadTimerRef.current);
    markReadTimerRef.current = setTimeout(doMark, 600);
  }, [currentChatId, myId, syncBadge, messagesHasMoreNewer]);

  const loadOlderMessages = React.useCallback(async () => {
    const chatId = currentChatId;
    if (!chatId) return;
    if (loadingOlderInFlightRef.current || loadingOlder) return;
    if (!messagesHasMore || !messagesCursor) return;

    const el = messagesContainerRef.current;
    if (!el) return;

    const cursorAtStart = messagesCursor;
    loadingOlderInFlightRef.current = true;
    setLoadingOlder(true);
    setShouldAutoscroll(false);

    try {
      const resp = await apiFetch(
        `${API_URL}/chats/${chatId}/messages?limit=${CHAT_MESSAGES_PAGE_SIZE}&before_id=${encodeURIComponent(cursorAtStart)}`
      );

      if (String(currentChatIdRef.current ?? '') !== String(chatId)) return;

      if (!resp.ok) {
        return;
      }

      const data = await resp.json();
      if (String(currentChatIdRef.current ?? '') !== String(chatId)) return;
      const older = Array.isArray(data?.messages) ? data.messages : [];

      historyPrependScrollLockRef.current = true;
      prependScrollRestoreRef.current = captureChatScrollAnchor(el);

      let prependAddedCount = 0;
      let prependedIds = [];
      let trimmedTailCursor = null;
      // Один commit: новые сообщения + снятие оверлея + restore scroll в ChatThread.useLayoutEffect (до paint).
      flushSync(() => {
        setChatMessages(prev => {
          const seen = new Set(prev.map(m => String(m.id)));
          const filtered = older
            .filter(m => !seen.has(String(m.id)))
            .map((m) => normalizeChatMessage(m, { __justPrepended: true }));
          prependAddedCount = filtered.length;
          prependedIds = filtered.map((m) => String(m.id));
          if (!filtered.length) return prev;
          const trimmed = trimChatWindowFromEnd([...filtered, ...prev]);
          if (trimmed.trimmed) {
            trimmedTailCursor = trimmed.cursor;
          }
          return trimmed.messages;
        });
        if (prependAddedCount > 0) {
          setMessagesAroundRevision((v) => v + 1);
          setLoadingOlder(false);
        }
      });

      setMessagesHasMore(!!data?.has_more);
      setMessagesCursor(data?.next_before_id ?? null);
      if (trimmedTailCursor != null) {
        setMessagesHasMoreNewer(true);
        setMessagesAfterCursor(trimmedTailCursor);
      }

      if (prependAddedCount === 0) {
        prependScrollRestoreRef.current = null;
        historyPrependScrollLockRef.current = false;
        setLoadingOlder(false);
        return;
      }

      window.setTimeout(() => {
        historyPrependScrollLockRef.current = false;
      }, HISTORY_PREPEND_SCROLL_LOCK_MS);

      if (prependedIds.length) {
        window.setTimeout(() => {
          setChatMessages((prev) => prev.map((m) => (
            prependedIds.includes(String(m.id))
              ? { ...m, __justPrepended: false }
              : m
          )));
        }, 260);
      }
    } finally {
      loadingOlderInFlightRef.current = false;
      setLoadingOlder(false);
    }
  }, [currentChatId, loadingOlder, messagesHasMore, messagesCursor]);

  const loadNewerMessages = React.useCallback(async () => {
    const chatId = currentChatId;
    if (!chatId) return;
    if (loadingNewerInFlightRef.current || loadingNewer) return;
    if (!messagesHasMoreNewer || !messagesAfterCursor) return;

    const el = messagesContainerRef.current;
    if (!el) return;

    const afterCursorAtStart = messagesAfterCursor;
    loadingNewerInFlightRef.current = true;
    setLoadingNewer(true);

    try {
      const resp = await apiFetch(
        `${API_URL}/chats/${chatId}/messages?limit=${CHAT_MESSAGES_PAGE_SIZE}&after_id=${encodeURIComponent(afterCursorAtStart)}`
      );

      if (String(currentChatIdRef.current ?? '') !== String(chatId)) return;

      if (!resp.ok) {
        return;
      }

      const data = await resp.json();
      if (String(currentChatIdRef.current ?? '') !== String(chatId)) return;
      const newer = Array.isArray(data?.messages) ? data.messages : [];

      historyAppendScrollLockRef.current = true;
      prependScrollRestoreRef.current = {
        ...captureChatScrollAnchor(el),
        skipPrependStabilize: true,
      };

      let appendAddedCount = 0;
      let trimmedHeadCursor = null;
      let lastKeptMessageId = null;
      flushSync(() => {
        setChatMessages((prev) => {
          const seen = new Set(prev.map((m) => String(m.id)));
          const filtered = newer
            .filter((m) => !seen.has(String(m.id)))
            .map((m) => normalizeChatMessage(m));
          appendAddedCount = filtered.length;
          if (!filtered.length) return prev;
          const trimmed = trimChatWindowFromStart([...prev, ...filtered]);
          if (trimmed.trimmed) {
            trimmedHeadCursor = trimmed.cursor;
          }
          lastKeptMessageId = trimmed.messages.length
            ? trimmed.messages[trimmed.messages.length - 1].id
            : null;
          return trimmed.messages;
        });
        if (appendAddedCount > 0) {
          setMessagesAroundRevision((v) => v + 1);
        }
        setLoadingNewer(false);
      });

      setMessagesHasMoreNewer(!!(data?.has_more_newer));
      setMessagesAfterCursor(
        data?.next_after_id ?? (lastKeptMessageId ?? (newer.length ? newer[newer.length - 1].id : messagesAfterCursor)),
      );
      if (trimmedHeadCursor != null) {
        setMessagesHasMore(true);
        setMessagesCursor(trimmedHeadCursor);
      }

      if (appendAddedCount === 0) {
        prependScrollRestoreRef.current = null;
        historyAppendScrollLockRef.current = false;
        return;
      }

      window.setTimeout(() => {
        historyAppendScrollLockRef.current = false;
      }, 320);
    } finally {
      loadingNewerInFlightRef.current = false;
      setLoadingNewer(false);
    }
  }, [currentChatId, loadingNewer, messagesHasMoreNewer, messagesAfterCursor]);



  // скроллим к сообщению-«якорю» (из URL или вычисленного firstUnread).
  // Если якорь — последнее сообщение, выравниваем к НИЗУ и после подтверждения дожимаем до конца.
  useEffect(() => {
    if (!msgFromLink) return;

    // является ли якорь последним сообщением в текущем списке
    const isLastAnchor =
      Array.isArray(chatMessages) &&
      chatMessages.length > 0 &&
      String(chatMessages[chatMessages.length - 1].id) === String(msgFromLink);

    const container = messagesContainerRef.current;
    // видим «успехом», если элемент ХОТЬ КАК-ТО пересёкся с видимой областью контейнера
    const isAnchorVisible = (el) => {
      if (!el || !container?.isConnected) return false;
      const cr = container.getBoundingClientRect();
      const br = el.getBoundingClientRect();
      return br.bottom > cr.top && br.top < cr.bottom;
    };

    const tryScroll = (behavior = 'smooth') => {
      const el = document.getElementById(`msg-${msgFromLink}`);
      if (!el || !container?.isConnected) return false;
      if (isLastAnchor) {
        scrollMessagesContainerToBottom(behavior === 'smooth' ? 'smooth' : 'auto');
        return true;
      }
      const cr = container.getBoundingClientRect();
      const er = el.getBoundingClientRect();
      const targetTop = container.scrollTop + (er.top - cr.top)
        - (container.clientHeight - el.offsetHeight) / 2;
      try {
        container.scrollTo({ top: Math.max(0, targetTop), behavior });
      } catch {
        container.scrollTop = Math.max(0, targetTop);
      }
      return true;
    };

    const clearAnchorUrl = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete('m');
      url.searchParams.delete('msg');
      const qs = url.searchParams.toString();
      navigate(url.pathname + (qs ? `?${qs}` : '') + (url.hash || ''), { replace: true });
    };

    // 1) первая попытка (плавно)
    if (tryScroll("smooth")) {
      console.log('[autoscroll] anchor element found, awaiting visibility:', msgFromLink);
      // подождём стабилизации, проверим, что якорь реально попал в вьюпорт
      const confirm = setInterval(() => {
        const el = document.getElementById(`msg-${msgFromLink}`);
        if (!el) return; // ждём
        if (isAnchorVisible(el)) {
          clearInterval(confirm);
          console.log('[autoscroll] anchor visible, clearing anchor:', msgFromLink);
          // ✨ даём «вспышку» подсветки на 3s
          setFlashMessageId(String(msgFromLink));
          setTimeout(() => setFlashMessageId(null), FLASH_MESSAGE_MS);
          // если якорь — последнее сообщение, дожмём строго к концу списка
          if (isLastAnchor) {
            scrollMessagesContainerToBottom('auto');
          }
          setShouldAutoscroll(false);
          // ✅ подтверждено — можно сбрасывать якорь и чистить URL
          setMsgFromLink(null);
          clearAnchorUrl();
        }
      }, 80);
      // через 1.2s — форс-дожим без анимации, если элемент ещё не в зоне видимости
      const hardstop = setTimeout(() => {
        clearInterval(confirm);
        const el = document.getElementById(`msg-${msgFromLink}`);
        if (el) {
          if (!isAnchorVisible(el)) tryScroll("auto");
          // 🔥 ФОЛБЭК: даже если confirm-пул всё ещё не поймал видимость,
          // запустим краткую вспышку здесь (элемент уже максимально дожат).
          setFlashMessageId(String(msgFromLink));
          setTimeout(() => setFlashMessageId(null), FLASH_MESSAGE_MS);
          setShouldAutoscroll(false);
        } else {
          // Элемент пропал из DOM после начала скролла — восстанавливаем автоскролл
          console.warn('[autoscroll] anchor element disappeared from DOM in hardstop, clearing:', msgFromLink);
          setShouldAutoscroll(true);
        }
        // и сбросим якорь/URL, чтобы не мигал повторно
        console.log('[autoscroll] hardstop fired, clearing anchor:', msgFromLink);
        setMsgFromLink(null);
        clearAnchorUrl();
      }, 1200);
      // защита от утечек
      return () => { clearInterval(confirm); clearTimeout(hardstop); };
    }
    // 2) элемент ещё не отрендерен — ретраи
    console.log('[autoscroll] anchor element not in DOM yet, retrying:', msgFromLink);
    const interval = setInterval(() => {
      if (tryScroll("auto")) {
        console.log('[autoscroll] anchor element appeared after retry, scrolling:', msgFromLink);
        clearInterval(interval);
      }
    }, 120);
    // Элемент так и не появился — сбрасываем якорь, иначе scrollAnchorActive зависнет навсегда.
    const timeout = setTimeout(() => {
      clearInterval(interval);
      console.warn('[autoscroll] anchor element never appeared in DOM after 1.8s, clearing anchor:', msgFromLink);
      setMsgFromLink(null);
      setShouldAutoscroll(true);
    }, 1800);
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [chatMessages, msgFromLink, navigate, scrollMessagesContainerToBottom]);

  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (!isCreateOpen) {
      if (isIOS) {
        html.style.overflow = '';
        body.style.overflow = '';
        body.style.touchAction = '';
      } else {
        const scrollY = body.style.top;
        body.style.position = '';
        body.style.top = '';
        body.style.left = '';
        body.style.right = '';
        body.style.width = '';
        if (scrollY) window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
      }
      return undefined;
    }

    if (isIOS) {
      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
      body.style.touchAction = 'none';
      return () => {
        html.style.overflow = '';
        body.style.overflow = '';
        body.style.touchAction = '';
        window.scrollTo(0, 0);
      };
    }

    const scrollY = window.scrollY;
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';

    return () => {
      const top = body.style.top;
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      window.scrollTo(0, parseInt(top || '0', 10) * -1);
    };
  }, [isCreateOpen]);

  const canRemoveSelfFromEditParticipants = (prev) => {
    const me = Number(myId);
    const admins = prev.filter((p) => !!p.chatAdmin);
    const adminsCount = admins.length;
    const iAmAdmin = admins.some((p) => String(p.id) === String(me));

    if (role === 'admin' && adminsCount === 0) {
      showToast('Убедитесь, что вы назначили админа чата');
      return false;
    }
    if (iAmAdmin && adminsCount === 1) {
      showToast('Убедитесь, что вы назначили админа чата');
      return false;
    }
    return true;
  };

  const onEditParticipantsChange = (next) => {
    const deduped = dedupeParticipantsById(next);
    setEditParticipants((prev) => {
      const me = Number(myId);
      const iWasIn = prev.some((p) => String(p.id) === String(me));
      const iAmIn = deduped.some((p) => String(p.id) === String(me));
      if (iWasIn && !iAmIn && !canRemoveSelfFromEditParticipants(prev)) {
        return prev;
      }
      return deduped;
    });
  };
  const onEditAvatarChange = (e) => {
    const f = e.target.files?.[0] || null;
    e.target.value = '';
    setEditAvatarFile(f);

    // сразу показать превью: если был прежний blob, освобождаем
    setEditAvatarPreviewUrl((prev) => {
      if (prev && prev.startsWith('blob:')) {
        try { URL.revokeObjectURL(prev); } catch { }
      }
      return f ? URL.createObjectURL(f) : prev;
    });
  };

  const onNewChatAvatarChange = (e) => {
    const f = e.target.files?.[0] ?? null;
    e.target.value = '';
    setNewChatAvatar(f);
    setNewChatAvatarPreviewUrl((prev) => {
      if (prev && prev.startsWith('blob:')) {
        try { URL.revokeObjectURL(prev); } catch { }
      }
      return f ? URL.createObjectURL(f) : null;
    });
  };

  const saveEditChat = async () => {
    if (!editChatId) return;
    try {
      const fd = new FormData();
      if (editChatName != null) fd.append('name', editChatName.trim());
      editParticipants.forEach(p => fd.append('participant_ids', String(p.id)));
      if (editAvatarFile) fd.append('file', editAvatarFile);

      const resp = await apiFetch(`${API_URL}/chats/${editChatId}`, { method: 'PATCH', body: fd });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(t || 'Не удалось сохранить изменения');
      }
      const updated = await resp.json();

      // локально обновим превью
      setUserChats(prev => prev.map(c =>
        c.id === editChatId ? {
          ...c,
          name: updated.name ?? c.name,
          avatar_url: updated.avatar_url ?? updated.avatar ?? c.avatar_url
        } : c
      ));

      showToast('Изменения сохранены');
      setEditOpen(false);
    } catch (e) {
      showToast(e.message || 'Ошибка сохранения');
    }
  };

  // Назначить / снять админа чата для участника
  const toggleChatAdmin = async (chatId, employeeId, chatAdminFlag) => {
    if (!chatAdminFlag) {
      const admins = editParticipants.filter((p) => !!p.chatAdmin);
      if (
        admins.length === 1
        && admins.some((p) => String(p.id) === String(employeeId))
      ) {
        showToast('Сначала назначьте другого админа чата');
        return;
      }
    }
    try {
      const resp = await apiFetch(`${API_URL}/chats/${chatId}/participants/${employeeId}/admin`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chat_admin: !!chatAdminFlag }),
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(t || 'Не удалось изменить права участника');
      }
      // локально помечаем в списке участников модалки
      setEditParticipants((prev) =>
        prev.map((p) =>
          String(p.id) === String(employeeId) ? { ...p, chatAdmin: !!chatAdminFlag } : p
        )
      );
      showToast('Права участника обновлены');
    } catch (e) {
      showToast(e.message || 'Ошибка изменения прав');
    }
  };


  // роли выбранных собеседников, если сервер их вернул в поиске
  const targetRoles = useMemo(
    () => selectedParticipants.map((p) => p.role).filter(Boolean),
    [selectedParticipants]
  );

  const precheck = canStartChat(role, targetRoles);
  const disabledReason = useMemo(() => {
    if (precheck === false) {
      if (role === "employee") return "Сотрудник может начать чат только с сотрудником";
      if (role === "employer") return "Руководитель может начать чат с employee/employer/admin";
      return "Недостаточно прав";
    }
    return null;
  }, [precheck, role]);

  // ===== СОРТИРОВКА: сначала чаты с сообщениями, затем без; внутри — по дате последнего сообщения =====
  const hasMsg = (c) =>
    Boolean(c.last_message_text || c.last_message || c.lastMessageText || c.last_message_time);

  const lastTs = (c) =>
    new Date(
      c.last_message_time || c.last_message_at || c.lastMessageAt || 0
    ).getTime();

  // ФИЛЬТРАЦИЯ СПИСКА ЧАТОВ (с сортировкой отображения)
  const filteredChats = useMemo(() => {
    // если где-то остался фильтр 'rank', трактуем его как 'group'
    const effectiveFilter = filter === 'rank' ? 'group' : filter;

    let list = userChats;
    if (effectiveFilter === 'private') {
      list = userChats.filter((c) => normalizeType(c.type) === 'private' && !isFavoritesChat(c));
    } else if (effectiveFilter === 'group') {
      list = userChats.filter((c) => {
        const t = normalizeType(c.type);
        return (t === 'group' || t === 'rank') && !isFavoritesChat(c);
      });
    }
    // 🔎 фильтр по названию/титулу (дебаунс применяется выше)
    const q = debouncedChatSearch;
    if (q) {
      list = list.filter((c) => {
        if (isFavoritesChat(c)) return FAVORITES_CHAT_TITLE.toLowerCase().includes(q);
        return ((c.title || c.name || '') + '').toLowerCase().includes(q);
      });
    }

    // не мутируем исходный массив
    return list.slice().sort((a, b) => {
      const aFav = isFavoritesChat(a);
      const bFav = isFavoritesChat(b);
      if (aFav !== bFav) return aFav ? -1 : 1;

      // 1) чаты с сообщениями — первыми
      const byHas = Number(hasMsg(b)) - Number(hasMsg(a));
      if (byHas !== 0) return byHas;

      // 2) затем — по убыванию времени последнего сообщения
      const byTime = lastTs(b) - lastTs(a);
      if (byTime !== 0) return byTime;

      // 3) стабильный третий ключ — по названию
      const nameA = (a.title || a.name || '').toLowerCase();
      const nameB = (b.title || b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [userChats, filter, debouncedChatSearch]);

  const createPrivateAndOpen = async (virtual) => {
    try {
      const createdId = await ensurePrivateChatFromVirtual(virtual);
      setCurrentChatId(createdId);
      navigate(`/chats?open=${createdId}`);
    } catch (e) {
      showToast(e.message || "Не удалось создать чат");
    }
  };

  const ensurePrivateChatFromVirtual = async (virtual) => {
    if (!virtual?.__virtual) return virtual?.id;

    const fd = new FormData();
    fd.append('name', virtual.name || '');
    fd.append('chat_type', 'private');
    fd.append('participant_ids', String(virtual.__targetEmployeeId));
    const response = await apiFetch(`${API_URL}/chats/`, { method: 'POST', body: fd });

    if (response.status === 409) {
      // если бэк ответил конфликтом — перечитаем список и найдём личку по ID собеседника
      const res2 = await apiFetch(`${API_URL}/chats/`);
      if (res2.ok) {
        const all = await res2.json();
        const normalizedAll = (Array.isArray(all) ? all : []).map((chat) => ({
          ...chat,
          is_muted: Boolean(chat?.is_muted),
        }));
        const found = normalizedAll.find(
          c => normalizeType(c.type) === 'private' && String(getPeerEmployeeId(c)) === String(virtual.__targetEmployeeId)
        );
        if (found) {
          setUserChats(prev => {
            const realIds = new Set(normalizedAll.map((chat) => String(chat.id)));
            const kept = prev.filter(c => String(c.id) !== String(virtual.id) && !realIds.has(String(c.id)));
            return [...kept, ...normalizedAll];
          });
          return found.id;
        }
      }
      throw new Error("Чат уже существует");
    }

    if (!response.ok) {
      const msg = await response.text();
      throw new Error(msg || "Не удалось создать чат");
    }

    const created = await response.json();
    // заменим виртуальную карточку на реальную / обновим добавленный сокетом чат
    setUserChats(prev => {
      // убираем виртуал
      const filtered = prev.filter(c => c !== virtual);
      // если сокет уже добавил реальный чат — обновим его
      const i = filtered.findIndex(c => String(c.id) === String(created.id));
      const patch = {
        id: created.id,
        name: created.name,
        type: normalizeType(created.chat_type ?? created.type ?? 'private'),
        last_message_text: null,
        last_message_time: null,
        unread_count: 0,
        is_muted: Boolean(created?.is_muted),
        avatar_url: created.avatar || created.avatar_url || virtual.avatar_url || null,

        // ✅ если бэк уже вернул participant_ids — используем их,
        // иначе соберём сами (для private)
        participant_ids: Array.isArray(created.participant_ids)
          ? created.participant_ids
          : [Number(myId), Number(virtual.__targetEmployeeId)].filter(Number.isFinite),
      };

      if (i >= 0) {
        const next = filtered.slice();
        next[i] = { ...next[i], ...patch };
        return next;
      }
      // иначе добавим новый
      return [...filtered, patch];
    });
    return created.id;
  };

  const handleCreateChat = async (e) => {
    e.preventDefault();

    // Создание group/rank чатов разрешаем по permission
    if (!canManageChats) return;

    if (!newChatName.trim()) {
      showToast("Введите название чата");
      return;
    }

    const participantIds = selectedParticipants.map(p => p.id);
    try {
      setLoading(true);
      setError(null);

      const fd = new FormData();
      fd.append('name', newChatName.trim());

      if (chatType === 'rank') {
        if (!rankValues.length) {
          showToast("Выберите хотя бы один ранг");
          setLoading(false);
          return;
        }
        fd.append('chat_type', 'rank');
        // rankValues теперь это массив ID (чисел/строк-чисел)
        rankValues.forEach(id => fd.append('rank_ids', String(id)));
      } else {
        fd.append('chat_type', 'group');
        participantIds.forEach(id => fd.append('participant_ids', String(id)));
      }

      if (newChatAvatar) fd.append('avatar', newChatAvatar);

      const response = await apiFetch(`${API_URL}/chats/`, {
        method: 'POST',
        body: fd,
      });

      if (response.status === 403) {
        let detail = '';
        try { detail = (await response.json())?.detail || ''; } catch { }
        showToast(detail || 'Недостаточно прав для создания этого чата');
        return;
      }

      if (!response.ok) {
        const msg = await response.text();
        throw new Error(msg || "Не удалось создать чат");
      }

      const newChat = await response.json();

      // Сбрасываем форму
      setNewChatName('');
      setNewChatAvatar(null);
      setNewChatAvatarPreviewUrl((prev) => {
        if (prev && prev.startsWith('blob:')) {
          try { URL.revokeObjectURL(prev); } catch { }
        }
        return null;
      });
      setSelectedParticipants([]);
      setRankValues([]);

      // Не открываем чат, если это ранговый (админ может не быть участником)
      // Опционально: если бэк начнёт возвращать participant_ids, можно открыть,
      // только если текущий пользователь действительно в участниках.
      const amIParticipant = Array.isArray(newChat.participant_ids)
        ? newChat.participant_ids.map(Number).includes(Number(myId))
        : (chatType !== 'rank');

      if (amIParticipant) {
        setCurrentChatId(newChat.id);
      }
      setCreateOpen(false);
      showToast("Чат создан");
    } catch (err) {
      setError(err.message);
      showToast(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Открыть модалку редактирования: тянем текущее имя и участников
  const openEditChat = async (chat) => {
    if (!chat || !chat.id) return;
    try {
      setEditChatId(chat.id);
      setEditChatName(chat.name || '');

      // подгрузим участников
      const resp = await apiFetch(`${API_URL}/chats/${chat.id}/participants`);
      const list = resp.ok ? await resp.json() : [];
      // ожидаем [{employee_id, name, role, avatar, chat_admin}]
      setEditParticipants(
        dedupeParticipantsById(
          (list || []).map((p) => ({
            id: Number(p.employee_id),
            name: p.name || p.full_name || `Пользователь ${p.employee_id}`,
            role: p.role || null,
            avatar: p.avatar || p.avatar_url || null,
            chatAdmin: !!p.chat_admin,
          }))
        )
      );

      setEditAvatarFile(null);

      // показать текущий аватар чата в круглой кнопке до выбора нового файла
      const currentAvatar =
        (chat.avatar_url || chat.avatar)
          ? resolveImageUrl(chat.avatar_url || chat.avatar)
          : null;
      setEditAvatarPreviewUrl(currentAvatar);

      setRoleMenuFor(null);
      setEditOpen(true);
    } catch {
      setEditOpen(true);
    }
  };


  // === Загрузка нового аватара для группы (✎) ===
  const openEditAvatar = (chatId) => {
    setEditAvatarChatId(chatId);
    editFileRef.current?.click();
  };

  const onEditFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // сбросить инпут
    if (!file || !editAvatarChatId) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await apiFetch(`${API_URL}/chats/${editAvatarChatId}/avatar`, {
        method: 'POST',
        body: fd
      });
      if (!resp.ok) {
        const msg = await resp.text();
        throw new Error(msg || 'Не удалось обновить аватар');
      }
      const data = await resp.json();
      const url = data.avatar_url || null;
      setUserChats(prev => prev.map(c => c.id === editAvatarChatId ? { ...c, avatar_url: url } : c));
    } catch (err) {
      showToast(err.message);
    } finally {
      setEditAvatarChatId(null);
    }
  };

  // === Удаление чата (только admin и только group) ===
  const deleteChat = async (chatId) => {
    if (!chatId) return;
    if (!canDeleteChats) return;
    const target = userChatsRef.current.find((c) => String(c.id) === String(chatId));
    if (isFavoritesChat(target)) {
      showToast('Чат «Избранное» нельзя удалить');
      return;
    }
    if (!window.confirm("Удалить этот чат? Действие необратимо.")) return;
    try {
      const resp = await apiFetch(`${API_URL}/chats/${chatId}`, { method: 'DELETE' });
      if (!resp.ok && resp.status !== 204) {
        const msg = await resp.text();
        throw new Error(msg || 'Не удалось удалить чат');
      }
      // локально удаляем из списка
      setUserChats(prev => prev.filter(c => String(c.id) !== String(chatId)));
      if (String(currentChatIdRef.current) === String(chatId)) {
        setCurrentChatId(null);
        setChatMessages([]);
      }
      showToast("Чат удалён");
    } catch (err) {
      showToast(err.message || 'Ошибка удаления чата');
    }
  };

  const toggleChatMute = async (chatId, nextMuted) => {
    if (!chatId) return;
    const prevChat = userChatsRef.current.find((c) => String(c.id) === String(chatId));
    if (!prevChat || prevChat.__virtual) return;

    const previousMuted = Boolean(prevChat.is_muted);
    const targetMuted = Boolean(nextMuted);
    if (previousMuted === targetMuted) return;

    setUserChats((prev) => prev.map((c) => (
      String(c.id) === String(chatId)
        ? { ...c, is_muted: targetMuted }
        : c
    )));

    try {
      const resp = await apiFetch(`${API_URL}/chats/${chatId}/mute`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_muted: targetMuted }),
      });
      if (!resp.ok) {
        const msg = await resp.text();
        throw new Error(msg || 'Не удалось обновить настройки уведомлений');
      }

      let payload = null;
      try {
        payload = await resp.json();
      } catch {
        payload = null;
      }

      if (payload?.is_muted != null) {
        const serverMuted = Boolean(payload.is_muted);
        setUserChats((prev) => prev.map((c) => (
          String(c.id) === String(chatId)
            ? { ...c, is_muted: serverMuted }
            : c
        )));
      }
    } catch (err) {
      setUserChats((prev) => prev.map((c) => (
        String(c.id) === String(chatId)
          ? { ...c, is_muted: previousMuted }
          : c
      )));
      showToast(err.message || 'Не удалось обновить настройки уведомлений');
    }
  };

  // ===================== ДЕЙСТВИЯ С СООБЩЕНИЕМ ======================
  function canDeleteMessage(msg) {
    if (!msg) return false;
    return canDeleteAnyMessages || String(msg.sender_id) === String(myId);
  }

  function canEditMessage(msg) {
    if (!msg) return false;
    if (msg.forwarded_from_message_id) return false;
    return String(msg.sender_id) === String(myId);
  }

  const openActionBar = (msg) => {
    if (!msg || msg.deleted_at) return;
    setSelectedMessage(msg);
    setActionBarOpen(true);
    setMultiSelectMode(false);
  };
  const closeActionBar = () => {
    setActionBarOpen(false);
    setSelectedMessage(null);
    setSelectedMessageIds([]);
    setMultiSelectMode(false);
  };

  const handleMessageClick = (msg) => {
    if (!msg || msg.deleted_at || isEditingMessageId === msg.id) return;

    setActionBarOpen(true);
    setSelectedMessage(msg);

    setSelectedMessageIds(prev => {
      const has = prev.includes(msg.id);
      let next;
      if (multiSelectMode) {
        // В режиме «Выделить» клики добавляют/снимают элементы.
        next = has ? prev.filter(id => id !== msg.id) : [...prev, msg.id];
      } else {
        // Обычный режим: только один primary.
        next = has ? prev.filter(id => id !== msg.id) : [msg.id];
      }

      if (next.length === 0) {
        setActionBarOpen(false);
        setSelectedMessage(null);
        setMultiSelectMode(false);
      }
      return next;
    });
  };

  const enableMultiSelectForMessage = (msg) => {
    if (!msg || msg.deleted_at) return;
    setSelectedMessage(msg);
    setActionBarOpen(true);
    setMultiSelectMode(true);
    setSelectedMessageIds((prev) => (prev.includes(msg.id) ? prev : [msg.id]));
  };


  // Ответить
  const handleReply = () => {
    if (selectedMessageIds.length !== 1 || !selectedMessage) return;

    setReplyDraft({
      id: selectedMessage.id,
      author: selectedMessage.sender_name || `Пользователь ${selectedMessage.sender_id}`,
      replyTo: {
        content: selectedMessage.content,
        attachments: selectedMessage.attachments,
        deleted_at: selectedMessage.deleted_at,
      },
    });

    closeActionBar();
  };

  // Копировать
  const handleCopy = async () => {
    if (selectedCount === 0) return;

    const texts = selectedMessages
      .map(m => (m?.content || '').trim())
      .filter(Boolean);

    if (texts.length === 0) {
      showToast('Нет текста для копирования');
      closeActionBar();
      return;
    }

    const combined = texts.join('\n\n');
    try {
      await navigator.clipboard.writeText(combined);
      showToast('Скопировано');
    } catch {
      showToast('Не удалось скопировать');
    } finally {
      closeActionBar();
    }
  };

  // Удалить
  const handleDelete = async () => {
    if (selectedCount === 0) return;

    // удаляем только те, которые разрешено
    const idsToDelete = selectedMessages
      .filter(canDeleteMessage)
      .map(m => m.id);

    if (idsToDelete.length === 0) {
      showToast('Недостаточно прав для удаления');
      closeActionBar();
      return;
    }

    try {
      const resp = await apiFetch(`${API_URL}/chats/${currentChatId}/messages/bulk_delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message_ids: idsToDelete }),
      });
      if (!resp.ok) throw new Error(await resp.text());

      const data = await resp.json();
      const deletedIds = data?.deleted_ids || idsToDelete;

      setChatMessages(prev => prev.filter(m => !deletedIds.includes(m.id)));
      setReplyDraft(prev => (deletedIds.includes(prev?.id) ? null : prev));
      showToast(`Удалено: ${deletedIds.length}`);
    } catch (e) {
      showToast(e.message || 'Ошибка удаления');
    } finally {
      closeActionBar();
    }
  };

  // Редактировать
  const startEdit = () => {
    if (selectedMessageIds.length !== 1 || !selectedMessage) return;
    if (!canEditMessage(selectedMessage)) {
      showToast(
        selectedMessage?.forwarded_from_message_id
          ? 'Пересланные сообщения нельзя редактировать'
          : 'Можно редактировать только свои сообщения',
      );
      return;
    }
    setIsEditingMessageId(selectedMessage.id);
    setEditingText(selectedMessage.content || '');
    closeActionBar();
  };
  const cancelEdit = () => {
    setIsEditingMessageId(null);
    setEditingText('');
  };
  const startEditLastMessage = () => {
    const lastEditableMessage = [...chatMessages]
      .reverse()
      .find((msg) => msg && !msg.deleted_at && canEditMessage(msg));
    if (!lastEditableMessage) return;
    setIsEditingMessageId(lastEditableMessage.id);
    setEditingText(lastEditableMessage.content || '');
    closeActionBar();
  };
  const saveEdit = async (msg) => {
    const text = (editingText || '').trim();
    if (!text) {
      showToast('Введите текст');
      return;
    }
    try {
      const resp = await apiFetch(`${API_URL}/chats/${currentChatId}/messages/${msg.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: text })
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(t || 'Не удалось отредактировать');
      }
      const updated = await resp.json();
      setChatMessages(prev => prev.map(m => {
        if (m.id !== msg.id) return m;
        const merged = { ...m, ...updated };
        if (!Array.isArray(updated.reactions)) {
          merged.reactions = Array.isArray(m.reactions) ? m.reactions : [];
        }
        return merged;
      }));
      setIsEditingMessageId(null);
      setEditingText('');
    } catch (e) {
      showToast(e.message || 'Ошибка редактирования');
    }
  };

  // Переслать
  const openForward = () => {
    setForwardTargets([]);
    setForwardMessageIds(selectedMessageIds);
    forwardSourceMessagesRef.current = selectedMessageIds
      .map((id) => chatMessages.find((m) => Number(m.id) === Number(id)))
      .filter(Boolean);
    setForwardSourceChatId(currentChatId);
    setForwardCommentText('');

    setForwardOpen(false);
    setIsForwardSelectMode(true);
    setCurrentChatId(null);

    closeActionBar();
  };
  const toggleForwardTarget = (chatId) => {
    setForwardTargets(prev => prev.includes(chatId) ? prev.filter(id => id !== chatId) : [...prev, chatId]);
  };
  const submitForward = async () => {
    if (!forwardMessageIds.length || forwardTargets.length === 0) {
      forwardSourceMessagesRef.current = [];
      setIsForwardSelectMode(false);
      setForwardTargets([]);
      setForwardMessageIds([]);
      setForwardSourceChatId(null);
      setForwardCommentText('');
      return;
    }

    const commentTrimmed = (forwardCommentText || '').trim();
    const selectedTargetsSnapshot = [...forwardTargets];
    const sourceMsgs = forwardSourceMessagesRef.current.length
      ? forwardSourceMessagesRef.current
      : forwardMessageIds
          .map((id) => chatMessages.find((m) => Number(m.id) === Number(id)))
          .filter(Boolean);
    const lastSrc = sourceMsgs.length ? sourceMsgs[sourceMsgs.length - 1] : null;
    const forwardPreviewText = buildForwardPreviewText(lastSrc);
    const forwardPreviewTime = new Date().toISOString();

    const applyForwardPreview = (targetChatIds) => {
      setUserChats((prev) =>
        patchChatsAfterForward(prev, targetChatIds, forwardPreviewText, forwardPreviewTime)
      );
    };

    try {
      const resolvedTargetIds = [];
      for (const targetId of selectedTargetsSnapshot) {
        const targetChat = userChats.find(c => String(c.id) === String(targetId));
        const realTargetId = targetChat?.__virtual
          ? await ensurePrivateChatFromVirtual(targetChat)
          : targetId;

        if (realTargetId != null && realTargetId !== '') {
          resolvedTargetIds.push(realTargetId);
        }
      }
      const targetIdsSnapshot = Array.from(
        new Map(resolvedTargetIds.map((id) => [String(id), id])).values()
      );
      if (targetIdsSnapshot.length === 0) {
        throw new Error('Выберите чат для пересылки');
      }

      const resp = await apiFetch(
        `${API_URL}/chats/${(forwardSourceChatId || currentChatId)}/messages/forward_bulk`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message_ids: forwardMessageIds, target_chat_ids: targetIdsSnapshot }),
        }
      );
      if (!resp.ok) throw new Error(await resp.text());

      applyForwardPreview(targetIdsSnapshot);
      showToast('Переслано');

      if (commentTrimmed) {
        for (const cid of targetIdsSnapshot) {
          await handleSendMessage(
            { content: commentTrimmed, chat_id: cid, reply_to_message_id: null },
            null
          );
        }
      }
    } catch (e) {
      showToast(e.message || 'Ошибка пересылки');
    } finally {
      forwardSourceMessagesRef.current = [];
      setIsForwardSelectMode(false);
      setForwardTargets([]);
      setForwardMessageIds([]);
      setForwardSourceChatId(null);
      setForwardCommentText('');
    }
  };
  // ===================================================================

  // === ОПТИМИСТИЧНАЯ ОТПРАВКА СООБЩЕНИЙ ===
  const handleSendMessage = async (arg, maybeEvent) => {
    // Поддержка двух способов вызова:
    // 1) handleSendMessage(e)
    // 2) handleSendMessage({ content, attachments }, e)
    const isPayload =
      arg &&
      typeof arg === 'object' &&
      !('preventDefault' in arg) &&
      (Object.prototype.hasOwnProperty.call(arg, 'content') ||
        Object.prototype.hasOwnProperty.call(arg, 'attachments'));

    const payload = isPayload ? arg : null;
    const isImportant = payload ? !!payload.is_important : false;
    const targetChatId = payload?.chat_id ?? currentChatId;
    const e = isPayload ? maybeEvent : arg;
    if (e && typeof e.preventDefault === 'function') e.preventDefault();

    const textToSend = (payload ? (payload.content || '') : (newMessage || '')).trim();
    const attachments = payload ? (payload.attachments || []) : [];

    // Разрешаем отправку либо текста, либо вложений (или обоих)
    if ((!textToSend && attachments.length === 0) || !targetChatId) return;

    // защита от двойного сабмита (не влияет на очистку поля)
    if (sendingRef.current) return;
    sendingRef.current = true;

    const replyTo =
      payload && Object.prototype.hasOwnProperty.call(payload, 'reply_to_message_id')
        ? payload.reply_to_message_id
        : (replyDraft?.id ?? null);

    // 1) мгновенно кладём временное сообщение и чистим UI
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const optimisticMsg = {
      id: tempId,
      client_temp_id: tempId,
      content: textToSend,
      timestamp: new Date().toISOString(),
      sender_id: myId,
      sender_name: myDisplayName || 'Вы',
      reply_to_message_id: replyTo,
      forwarded_from_message_id: null,
      deleted_at: null,
      // добавим вложения в оптимистичное сообщение, если есть
      attachments: attachments.length ? attachments : undefined,
      is_important: isImportant,
      __optimistic: true,
      __failed: false,
      reactions: [],
    };

    const isTargetChatCurrentlyOpen = () =>
      String(targetChatId ?? '') === String(currentChatIdRef.current ?? '');

    if (isTargetChatCurrentlyOpen()) {
      setChatMessages(prev => {
        const trimmed = trimChatWindowFromStart([...prev, optimisticMsg]);
        if (trimmed.trimmed && trimmed.cursor != null) {
          setMessagesHasMore(true);
          setMessagesCursor(trimmed.cursor);
          setMessagesAroundRevision((v) => v + 1);
        }
        return trimmed.messages;
      });
    }
    // 🔽 Для активного чата: дожимаем вниз и чистим composer
    if (isTargetChatCurrentlyOpen()) {
      setForceScrollOnOwnSend(true);
      setNewMessage('');
      setReplyDraft(null);
    }

    // обновить превью карточки чата оптимистично; снимаем плашку непрочитанного при ответе
    setUserChats(prev =>
      prev.map(c => String(c.id) === String(targetChatId) ? {
        ...c,
        last_message_text: textToSend || (attachments.length ? '📎 вложение' : c.last_message_text),
        last_message_time: optimisticMsg.timestamp || c.last_message_time,
        unread_count: 0
      } : c)
    );

    // 2) отправляем на сервер (без изменения глобального loading)
    try {
      const body = {
        ...(textToSend ? { content: textToSend } : {}),
        ...(replyTo != null && replyTo !== '' ? { reply_to_message_id: replyTo } : {}),
        ...(attachments.length ? { attachments } : {}),
        ...(payload && 'is_important' in payload ? { is_important: !!payload.is_important } : {}),
      };

      const response = await apiFetch(`${API_URL}/chats/${targetChatId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // бэкенд берёт отправителя из текущего пользователя
        body: JSON.stringify(body),
      });

      if (response.status === 403) {
        let detail = '';
        try { detail = (await response.json())?.detail || ''; } catch { }
        throw new Error(detail || "Недостаточно прав для отправки сообщения");
      }

      if (!response.ok) {
        const msg = await response.text();
        throw new Error(msg || "Ошибка отправки сообщения");
      }

      const sentMessage = await response.json();

      // 3) успех — заменить временное на реальное
      if (isTargetChatCurrentlyOpen()) {
        setChatMessages(prev =>
          prev.map(m => m.client_temp_id === tempId ? normalizeChatMessage(sentMessage) : m)
        );
      }

      // превью чата уже обновили оптимистично
    } catch (err) {
      // 4) ошибка — помечаем временное как неотправленное
      if (isTargetChatCurrentlyOpen()) {
        setChatMessages(prev =>
          prev.map(m => m.client_temp_id === tempId ? { ...m, __optimistic: false, __failed: true } : m)
        );
      }
      setError(err.message);
      showToast(err.message);
    } finally {
      sendingRef.current = false;
    }
  };

  // Повторная отправка «упавшего» оптимистичного сообщения
  const retrySendMessage = async (failedMsg) => {
    if (!failedMsg || !failedMsg.__failed || !currentChatId) return;

    const newTempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    // "реанимируем" пузырь
    setChatMessages(prev =>
      prev.map(m => m.id === failedMsg.id ? { ...m, __failed: false, __optimistic: true, client_temp_id: newTempId } : m)
    );

    try {
      const body = failedMsg.reply_to_message_id
        ? { content: failedMsg.content, reply_to_message_id: failedMsg.reply_to_message_id }
        : { content: failedMsg.content };

      const resp = await apiFetch(`${API_URL}/chats/${currentChatId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(t || 'Не удалось отправить сообщение');
      }
      const real = await resp.json();
      setChatMessages(prev =>
        prev.map(m => m.client_temp_id === newTempId ? normalizeChatMessage(real) : m)
      );
      // превью оставляем как есть
    } catch (e) {
      setChatMessages(prev =>
        prev.map(m => m.client_temp_id === newTempId ? { ...m, __optimistic: false, __failed: true } : m)
      );
      showToast(e.message || 'Ошибка отправки');
    }
  };
  // ============================================

  // ======== READ STATUS (две галочки) ========
  const currentChat = useMemo(
    () => userChats.find(c => String(c.id) === String(currentChatId)) || null,
    [userChats, currentChatId]
  );
  const currentChatType = normalizeType(currentChat?.type) || (Object.keys(participantsLastReads || {}).length === 2 ? 'private' : 'group');

  const canPinOrUnpinInOpenChat = useMemo(() => {
    if (!currentChatId || !currentChat) return false;
    const t = (currentChatType || '').toLowerCase();
    if (t === 'private' || t === 'favorites') return true;
    if (role === 'admin') return true;
    return Boolean(currentChat.is_chat_admin || currentChat.isChatAdmin);
  }, [currentChatId, currentChat, currentChatType, role]);

  const handlePinMessage = React.useCallback(async (messageId) => {
    if (!currentChatId || messageId == null) return;
    try {
      const r = await apiFetch(
        `${API_URL}/chats/${currentChatId}/messages/${encodeURIComponent(messageId)}/pin`,
        { method: 'POST' },
      );
      if (!r.ok) {
        const txt = await r.text().catch(() => '');
        showToast(txt || 'Не удалось закрепить сообщение');
        return;
      }
      await refreshPinnedMessages(currentChatId);
      setPinnedMetaReady(true);
    } catch (e) {
      showToast(e?.message || 'Ошибка закрепления');
    }
  }, [currentChatId, refreshPinnedMessages, showToast]);

  const handleUnpinMessage = React.useCallback(async (messageId) => {
    if (!currentChatId || messageId == null) return;
    try {
      const r = await apiFetch(
        `${API_URL}/chats/${currentChatId}/messages/${encodeURIComponent(messageId)}/pin`,
        { method: 'DELETE' },
      );
      if (!r.ok) {
        const txt = await r.text().catch(() => '');
        showToast(txt || 'Не удалось открепить сообщение');
        return;
      }
      await refreshPinnedMessages(currentChatId);
      setPinnedMetaReady(true);
    } catch (e) {
      showToast(e?.message || 'Ошибка открепления');
    }
  }, [currentChatId, refreshPinnedMessages, showToast]);

  const isMessageReadByCounterparty = (msg) => {
    const myIdStr = String(myId);
    if (String(msg.sender_id) !== myIdStr) return false; // галочки рисуем только на своих сообщениях
    if (!participantsLastReads || typeof participantsLastReads !== 'object') return false;

    const msgId = Number(msg.id);
    if (!Number.isFinite(msgId)) return false;

    if (currentChatType === 'private') {
      const otherId = Object.keys(participantsLastReads).find(uid => String(uid) !== myIdStr);
      if (!otherId) return false;
      const last = Number(participantsLastReads[otherId] ?? -1);
      return Number.isFinite(last) && last >= msgId;
    }
    // group: если ЛЮБОЙ, кроме меня, прочитал
    return Object.entries(participantsLastReads).some(([uid, last]) => {
      if (String(uid) === myIdStr) return false;
      const n = Number(last ?? -1);
      return Number.isFinite(n) && n >= msgId;
    });
  };
  // ============================================

  // Закрытие action bar по ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setActionBarOpen(false);
        setSelectedMessage(null);
        setSelectedMessageIds([]);
        setForwardOpen(false);
        setIsForwardSelectMode(false);
        setForwardTargets([]);
        setForwardMessageIds([]);
        setForwardSourceChatId(null);
        setForwardCommentText('');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Сброс меню при смене чата
  useEffect(() => {
    setActionBarOpen(false);
    setSelectedMessage(null);
    setSelectedMessageIds([]);
    setIsEditingMessageId(null);
    setEditingText('');
  }, [currentChatId]);

  useEffect(() => {
    document.body.classList.add('chat-page-open');

    return () => {
      document.body.classList.remove('chat-page-open');
    };
  }, []);

  if (!isChatSwitching && !userChats.length)
    return <div className="loading">Загрузка чатов...</div>;

  return (
    <div className={s.page} style={{
      '--chat-sidebar-width': `${sidebarWidth}px`,
    }}>
      {/* скрытый инпут для смены аватара группы */}
      <input
        ref={editFileRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={onEditFileChange}
      />

      {/* Toast */}
      {toast.visible && (
        <div
          style={{
            position: 'fixed',
            top: 16,
            right: 16,
            background: '#1f2937',
            color: '#fff',
            padding: '10px 14px',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,.2)',
            zIndex: 3001,
            maxWidth: 360
          }}
          role="alert"
        >
          {toast.message}
        </div>
      )}

      {/* Левая колонка: список чатов (на десктопе фиксированная, на мобилке — полный экран) */}
      <div
        className={`${s.sidebarCol} ${isForwardSelectMode ? s.sidebarColForwardComposer : ''}`}
        ref={sidebarColRef}
      >
        <h2 className={s.pageTitle}>Чаты</h2>

        {error && (
          <div className={s.card}>
            <div className={s.inlineError}>Ошибка: {error}</div>
          </div>
        )}

        <ChatSidebar
          role={role}
          canManageChats={canManageChats}
          canDeleteChats={canDeleteChats}
          filter={filter}
          onChangeFilter={setFilter}
          loading={!idReady}

          // 🔎 поиск по чатам
          showChatSearch={showChatSearch}
          setShowChatSearch={setShowChatSearch}
          chatSearchQuery={chatSearch}
          setChatSearchQuery={setChatSearch}
          messageSearchItems={messageSearchItems}
          messageSearchLoading={messageSearchLoading}
          messageSearchHasMore={messageSearchHasMore}
          messageSearchLoadingMore={messageSearchLoadingMore}
          onLoadMoreMessageSearch={loadMoreMessageSearch}

          isForwardSelectMode={isForwardSelectMode}
          forwardTargets={forwardTargets}
          onCancelForwardSelect={() => {
            setIsForwardSelectMode(false);
            setForwardTargets([]);
            setForwardMessageIds([]);
            setForwardSourceChatId(null);
            setForwardCommentText('');
          }}
          forwardCommentText={forwardCommentText}
          setForwardCommentText={setForwardCommentText}
          onToggleForwardTarget={toggleForwardTarget}
          onSubmitForward={submitForward}

          filteredChats={filteredChats}
          chatDraftsByChatId={chatDraftsByChatId}
          currentChatId={currentChatId}
          getChatPresence={getChatPresence}
          onSelectChat={(id, messageId = null) => {
            const fromList = userChatsRef.current.find((c) => String(c.id) === String(id));
            const rxAnchor = fromList?.reaction_unread?.messageId;
            const targetAnchor = messageId ?? rxAnchor ?? null;
            if (targetAnchor) setMsgFromLink(String(targetAnchor));
            setCurrentChatId(id);
            if (targetAnchor) {
              navigate(`/chats?open=${id}&m=${targetAnchor}`, { state: { chatOpen: true } });
            } else {
              navigate(`/chats?open=${id}`, { state: { chatOpen: true } }); // помечаем «чат открыт»
            }
          }}
          onCreatePrivateFromVirtual={createPrivateAndOpen}

          resolveImageUrl={resolveImageUrl}
          formatTime={formatTime}
          hasMsg={hasMsg}

          editMenuChatId={editMenuChatId}
          setEditMenuChatId={setEditMenuChatId}
          menuContainerRef={menuContainerRef}
          onOpenEditAvatar={openEditAvatar}
          onDeleteChat={deleteChat}

          // ===== РЕДАКТИРОВАНИЕ ЧАТА =====
          onOpenEditChat={openEditChat}
          isEditOpen={isEditOpen}
          setEditOpen={setEditOpen}
          editChatName={editChatName}
          setEditChatName={setEditChatName}
          editParticipants={editParticipants}
          onEditParticipantsChange={onEditParticipantsChange}
          onSaveEditChat={saveEditChat}
          myEmployeeId={myId}
          editAvatarInputRef={editAvatarInputRef}
          onEditAvatarChange={onEditAvatarChange}
          editAvatarPreviewUrl={editAvatarPreviewUrl}
          // ▼ меню ролей участника
          roleMenuFor={roleMenuFor}
          setRoleMenuFor={setRoleMenuFor}
          toggleChatAdmin={toggleChatAdmin}
          editChatId={editChatId}

          // FAB "создать чат"
          onOpenCreateModal={async () => {
            setChatType('group');
            setSelectedParticipants([]);
            setNewChatName('');
            setNewChatAvatar(null);
            setNewChatAvatarPreviewUrl((prev) => {
              if (prev && prev.startsWith('blob:')) {
                try { URL.revokeObjectURL(prev); } catch { }
              }
              return null;
            });
            setRankValues([]);
            // подгрузим варианты рангов
            try {
              const r = await apiFetch(`${API_URL}/structure/ranks`); // новый эндпоинт
              const list = r.ok ? await r.json() : [];
              // ожидаем [{id, abbr, name, second_name, ...}]
              setRankOptions(Array.isArray(list) ? list : []);
            } catch {
              setRankOptions([]);
            }
            setCreateOpen(true);
          }}

          // Модалка создания
          isCreateOpen={isCreateOpen}
          setCreateOpen={setCreateOpen}
          newChatName={newChatName}
          setNewChatName={setNewChatName}
          createAvatarInputRef={createAvatarInputRef}
          newChatAvatarPreviewUrl={newChatAvatarPreviewUrl}
          onNewChatAvatarChange={onNewChatAvatarChange}
          selectedParticipants={selectedParticipants}
          onCreateParticipantsChange={setSelectedParticipants}
          precheck={precheck}
          disabledReason={disabledReason}
          onCreateChat={handleCreateChat}

          // пропсы для ранговых чатов (переключатель и мультиселект)
          chatType={chatType}
          setChatType={setChatType}
          rankOptions={rankOptions}
          rankValues={rankValues}
          setRankValues={setRankValues}
          onAvatarClick={handleAvatarClick}
          getPeerEmployeeId={getPeerEmployeeId}
          employeeTaskStatsMap={employeeTaskStatsMap}

          isMembersOpen={isMembersOpen}
          setIsMembersOpen={setIsMembersOpen}
          membersChatTitle={membersChatTitle}
          membersLoading={membersLoading}
          membersList={membersList}
        />
      </div>{/* /sidebarCol */}

      <div
        className={s.resizeHandle}
        onMouseDown={startResize}
        role="separator"
        aria-orientation="vertical"
        aria-label="Изменить ширину списка чатов"
        tabIndex={0}
      />

      <div className={s.threadCol} data-has-chat={Boolean(currentChatId).toString()}>
        <div className={s.threadEmpty}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 15L6.92474 18.1137C6.49579 18.548 6.28131 18.7652 6.09695 18.7805C5.93701 18.7938 5.78042 18.7295 5.67596 18.6076C5.55556 18.4672 5.55556 18.162 5.55556 17.5515V15.9916C5.55556 15.444 5.10707 15.0477 4.5652 14.9683C3.25374 14.7762 2.22378 13.7463 2.03168 12.4348C2 12.2186 2 11.9605 2 11.4444V6.8C2 5.11984 2 4.27976 2.32698 3.63803C2.6146 3.07354 3.07354 2.6146 3.63803 2.32698C4.27976 2 5.11984 2 6.8 2H14.2C15.8802 2 16.7202 2 17.362 2.32698C17.9265 2.6146 18.3854 3.07354 18.673 3.63803C19 4.27976 19 5.11984 19 6.8V11M19 22L16.8236 20.4869C16.5177 20.2742 16.3647 20.1678 16.1982 20.0924C16.0504 20.0255 15.8951 19.9768 15.7356 19.9474C15.5558 19.9143 15.3695 19.9143 14.9969 19.9143H13.2C12.0799 19.9143 11.5198 19.9143 11.092 19.6963C10.7157 19.5046 10.4097 19.1986 10.218 18.8223C10 18.3944 10 17.8344 10 16.7143V14.2C10 13.0799 10 12.5198 10.218 12.092C10.4097 11.7157 10.7157 11.4097 11.092 11.218C11.5198 11 12.0799 11 13.2 11H18.8C19.9201 11 20.4802 11 20.908 11.218C21.2843 11.4097 21.5903 11.7157 21.782 12.092C22 12.5198 22 13.0799 22 14.2V16.9143C22 17.8462 22 18.3121 21.8478 18.6797C21.6448 19.1697 21.2554 19.5591 20.7654 19.762C20.3978 19.9143 19.9319 19.9143 19 19.9143V22Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <p>Выберите чат, чтобы начать переписку</p>
        </div>

        <ChatThread
          isOpen={Boolean(currentChatId)}
          isChatSwitching={isChatSwitching}
          onClose={() => {
            setFirstUnreadId(null);
            clearTimeout(markReadTimerRef.current);
            // Всегда остаёмся на /chats, просто убираем параметры.
            // navigate(-1) нельзя использовать — при открытии по прямой ссылке
            // (пуш-уведомление) он уводит с роута и размонтирует компонент,
            // что обрывает сокет-соединение.
            navigate('/chats', { replace: true });
            setCurrentChatId(null);
          }}
          currentChat={currentChat}
          currentChatType={currentChatType}
          // ✅ presence для шапки личного чата
          counterpartyPresence={
            currentChatType === 'private' && !isFavoritesChat(currentChat)
              ? { show: true, online: isEmployeeOnline(getPeerEmployeeId(currentChat)) }
              : null
          }
          resolveImageUrl={resolveImageUrl}

          chatMessages={chatMessages}
          messagesEndRef={messagesEndRef}
          flashMessageId={flashMessageId}
          onFlashMessage={(id) => {
            setFlashMessageId(String(id));
            setTimeout(() => setFlashMessageId(null), FLASH_MESSAGE_MS);
          }}
          reactionJumpHint={reactionJumpHint}
          onConsumeReactionJumpHint={() => setReactionJumpHint(null)}
          unseenReactionMessageIds={unseenReactionMessageIds}
          onAckUnseenReactionMessage={(id) =>
            setUnseenReactionMessageIds((p) => p.filter((x) => String(x) !== String(id)))}
          messagesContainerRef={messagesContainerRef}
          prependScrollRestoreRef={prependScrollRestoreRef}
          messagesAroundRevision={messagesAroundRevision}
          onReachTop={loadOlderMessages}
          onReachBottom={loadNewerMessages}
          loadingOlder={loadingOlder}
          loadingNewer={loadingNewer}
          hasMore={messagesHasMore}
          hasMoreOlder={messagesHasMore}
          hasMoreNewer={messagesHasMoreNewer}
          // «умный» автоскролл: внутренняя медиа-загрузка будет скроллить вниз
          // только если пользователь уже у нижней границы
          autoscrollEnabled={shouldAutoscroll}
          scrollAnchorActive={Boolean(msgFromLink)}
          onMessagesScroll={handleMessagesScroll}
          firstUnreadId={firstUnreadId}
          myId={myId}
          isMessageReadByCounterparty={isMessageReadByCounterparty}
          onSearchModeChange={handleSearchModeChange}
          onLoadAroundForSearch={loadAroundForSearch}
          onJumpToLatestWindow={jumpToLatestMessageWindow}
          onRequestLiveBottom={requestLiveBottom}
          searchMode={isSearchMode}

          // панель действий
          actionBarOpen={actionBarOpen}
          multiSelectMode={multiSelectMode}
          selectedMessage={selectedMessage}
          onCloseActionBar={closeActionBar}
          onMessageClick={handleMessageClick}
          onEnableMultiSelect={enableMultiSelectForMessage}
          onReply={handleReply}
          onCopy={handleCopy}
          onDelete={handleDelete}
          onForward={openForward}
          onStartEdit={startEdit}
          onStartEditLastMessage={startEditLastMessage}
          canDeleteMessage={canDeleteMessage}
          canEditMessage={canEditMessage}

          // редактирование
          isEditingMessageId={isEditingMessageId}
          editingText={editingText}
          setEditingText={setEditingText}
          onCancelEdit={cancelEdit}
          onSaveEdit={saveEdit}

          selectedMessageIds={selectedMessageIds}
          selectedCount={selectedCount}
          selectionHasContent={selectionHasContent}
          deletableCount={deletableCount}

          // размер шрифта
          onDecFont={decFont}
          onIncFont={incFont}
          fontSize={msgFontSize}

          // ответ
          replyDraft={replyDraft}
          setReplyDraft={setReplyDraft}

          // отправка сообщения
          newMessage={newMessage}
          setNewMessage={setNewMessage}
          onSendMessage={handleSendMessage}
          isSending={sendingRef.current}
          onRetrySend={retrySendMessage}

          formatTime={formatTime}

          chatId={currentChatId}
          onPatchMessage={(messageId, patch) => {
            setChatMessages((prev) => prev.map((m) =>
              String(m.id) === String(messageId) ? { ...m, ...patch } : m
            ));
          }}
          onSuppressReactionToast={(messageId) => {
            reactionToastSuppressRef.current = { messageId: String(messageId), at: Date.now() };
          }}
          onOpenChatAvatarProfile={(chat) => {
            handleOpenAccountFromChat(chat);
          }}
          onOpenUserProfile={(employeeId) => {
            if (employeeId != null) navigate(`/account/${employeeId}`);
          }}
          getPeerEmployeeId={getPeerEmployeeId}
          employeeTaskStatsMap={employeeTaskStatsMap}
          showToast={showToast}
          onToggleChatMute={toggleChatMute}
          pinnedMessages={pinnedMessagesItems}
          canPinOrUnpin={canPinOrUnpinInOpenChat}
          onPinMessage={handlePinMessage}
          onUnpinMessage={handleUnpinMessage}
        />
      </div>

      {/* Нижний тапбар рендерится глобально в App */}
    </div>
  );
}

export default Chats;
